import {
  GoogleMap,
  LoadScript,
  Marker, MarkerClusterer, Polygon
} from "@react-google-maps/api";
import { debounce } from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";
import { Dropdown } from "react-bootstrap";
import { BsFillEnvelopeFill, BsGrid3X3GapFill } from "react-icons/bs";
import { FaSearch } from "react-icons/fa";
import { FiRefreshCw } from "react-icons/fi";
import { IoCloseCircle, IoFilterSharp, IoMap } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import ToastNotification from "./ToastNotification";
import MapLegend from "./radiusLegend";

const containerStyle = {
  width: "100%",
  height: "100%",
};

// Initial default center for the map
const defaultCenter = {
  lat: 37.7749,
  lng: -122.4194,
};

// Great-circle distance in miles (Haversine). Used to trim records whose
// zipcode falls within the radius but whose actual geocoded address is
// outside the circle.
const EARTH_RADIUS_MI = 3959;
const toRadians = (deg) => (deg * Math.PI) / 180;
const haversineMiles = (lat1, lng1, lat2, lng2) => {
  const dLat = toRadians(lat2 - lat1);
  const dLng = toRadians(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) * Math.sin(dLng / 2) ** 2;
  return EARTH_RADIUS_MI * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

// Format money values for display. The new Cons_TIV resolver returns
// decimal(19,4) which Sequelize surfaces as a string like "22766.0000";
// without this, cards would show the raw decimal string.
const formatMoney = (value) => {
  if (value === null || value === undefined || value === "") return "N/A";
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (Number.isNaN(num)) return String(value);
  return num.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
};

// Google Maps libraries - defined outside component to prevent unnecessary reloads
const googleMapsLibraries = ["places", "geometry", "drawing", "visualization"];

function GeographicalConcentration() {
  const navigate = useNavigate();
  const [radius, setRadius] = useState(5);
  const mapRef = useRef(null);
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const [location, setLocation] = useState("");
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [markerPosition, setMarkerPosition] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [hover, setHover] = useState(false);
  const [isBottomPanelOpen, setBottomPanelOpen] = useState(false);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [insuredsData, setInsuredsData] = useState([]);
  const [selectedInsured, setSelectedInsured] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [addressMarkers, setAddressMarkers] = useState([]);
  const [isGeocodingInProgress, setIsGeocodingInProgress] = useState(false);
  const [isMobileView, setIsMobileView] = useState(false);
  const [activeMarkerId, setActiveMarkerId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [zipcodes, setZipcodes] = useState([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [availableCarriers, setAvailableCarriers] = useState([]);
  const insuredListRef = useRef(null);
  // const [selectedCarrier, setSelectedCarrier] = useState("");
  const [selectedCarriers, setSelectedCarriers] = useState([]);
  const [totalInsuredsCount, setTotalInsuredsCount] = useState(0);
  const [specificControlNoRecord, setSpecificControlNoRecord] = useState(null);
  const [geocodingStats, setGeocodingStats] = useState({
    totalInsureds: 0,
    attempts: 0,
    successes: 0,
    failures: 0,
  });
  // --- Add these new state variables ---
  const [error, setError] = useState(null);
  const geocodingAbortController = useRef(null);
  
  // ===== FIX 1: Request ID Tracking System =====
  // WHY: Prevents race conditions where older API responses overwrite newer ones
  // HOW: Each search gets a unique ID; responses are validated against current request
  const currentRequestId = useRef(0);
  const activeSearchId = useRef(0);
  
  // ===== FIX 2: Abort Controllers for All Fetch Operations =====
  // WHY: Cancels in-flight requests when new searches are initiated
  // HOW: Each fetch type has its own abort controller that can be cancelled
  const abortControllers = useRef({
    zipcodes: null,
    markers: null,
    listings: null,
    carriers: null,
  });
  
  const apiCallsInProgress = useRef({
    zipcodes: false,
    markers: false,
    listings: false,
    carriers: false,
  });
  const [toasts, setToasts] = useState([]);
  const [isCarrierDataLoading, setIsCarrierDataLoading] = useState(false);
  const [zipcodeBoundaries, setZipcodeBoundaries] = useState([]);
  const [isLoadingBoundaries, setIsLoadingBoundaries] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const [availableStatuses, setAvailableStatuses] = useState([]);

  const [searchOnSelection, setSearchOnSelection] = useState(false);
  const [activeIndex, setActiveIndex] = useState(null);
  const [hasZoomedToFit, setHasZoomedToFit] = useState(false);
  const zipcodeBoundaryCache = useRef({});
  const boundaryApiInProgress = useRef(false);
  const [processedZipcodes, setProcessedZipcodes] = useState([]);
  const hasTriggeredRef = useRef(false);
  const apiCallInProgressRef = useRef(false);
  const [hasClaimsOnly, setHasClaimsOnly] = useState(false);
  const [hoveredControlNo, setHoveredControlNo] = useState(null);
  const [hoveredRecord, setHoveredRecord] = useState(null);

  const listItemRefs = useRef({});
  
  // ===== FIX: Add initialization tracking =====
  const isInitializing = useRef(true);
  const initializationComplete = useRef(false);
  
  // ===== FIX 20: Add Debounced Search Ref =====
  // WHY: Multiple rapid filter changes cause race conditions
  // HOW: Track last search parameters to avoid redundant searches
  const lastSearchParams = useRef({
    lat: null,
    lng: null,
    radius: 0,
    carriers: localStorage.getItem("currentFilterCarrier") || "",
    controlNo: localStorage.getItem("currentFilterControlNo") || "",
    status: localStorage.getItem("currentFilterStatus") || "Bound,Bind Order Received",
    hasClaimsOnly: localStorage.getItem("currentFilterHasClaimsOnly") === "true",
  });

  /********helpers*************/

  const setListItemRef = (controlNo) => (el) => {
    if (!controlNo) return;
    listItemRefs.current[controlNo] = el || undefined;
  };

  const scrollRowIntoView = (controlNo) => {
    const row = listItemRefs.current[controlNo];
    if (row && insuredListRef.current) {
      row.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest",
      });
    }
  };

  // --- Selected ControlNo state for highlight ---

  const [selectedControlNo, setSelectedControlNo] = useState(
    () => window.localStorage.getItem("currentFilterControlNo") || ""
  );

  // Update selectedControlNo whenever insuredsData changes (i.e., after search/filter)
  useEffect(() => {
    setSelectedControlNo(
      window.localStorage.getItem("currentFilterControlNo") || ""
    );
  }, [insuredsData]);

  const apiUrl = process.env.REACT_APP_API_URL;
  const mapApiKey = process.env.REACT_APP_MAP_API_KEY;
  const rapidApiKey = process.env.REACT_APP_RAPID_API_KEY;

  console.log("🔑 Map API Key loaded:", mapApiKey ? "✅ Yes" : "❌ No");
  console.log(rapidApiKey, "rapid api key");

  const dispatchFilterChangeEvent = (carrierName, controlNo) => {
    window.dispatchEvent(
      new CustomEvent("filter-params-changed", {
        detail: {
          carrier: carrierName,
          controlNo: controlNo,
        },
      })
    );
  };

  // ===== FIX: CONSOLIDATED INITIALIZATION - Single useEffect for all initial state =====
  // WHY: Multiple useEffects each trigger the search effect, causing cascading requests
  // HOW: Load all localStorage values at once, React batches the state updates
  useEffect(() => {
    console.log("🚀 Initializing component - loading saved state");
    
    // Check authentication first
    const token = localStorage.getItem("accessToken");
    if (!token) {
      navigate("/");
      return;
    }

    // Load ALL saved state values at once
    try {
      const savedCarrier = localStorage.getItem("currentFilterCarrier");
      const savedControlNo = localStorage.getItem("currentFilterControlNo");
      const savedStatus = localStorage.getItem("currentFilterStatus");
      const savedHasClaimsOnly = localStorage.getItem("currentFilterHasClaimsOnly");
      const savedZipcodes = localStorage.getItem("currentZipcodes");

      // Set carriers
      if (savedCarrier) {
        const carrierArray = savedCarrier
          .split(",")
          .filter((carrier) => carrier.trim() !== "");
        setSelectedCarriers(carrierArray);
      }

      // Set control number in input
      if (savedControlNo && document.getElementById("ControlNo")) {
        document.getElementById("ControlNo").value = savedControlNo;
      }

      // Set status with defaults
      if (savedStatus) {
        const statusArray = savedStatus
          .split(",")
          .filter((status) => status.trim() !== "");

        if (statusArray.length === 0) {
          setSelectedStatuses(["Bound", "Bind Order Received"]);
          localStorage.setItem("currentFilterStatus", "Bound,Bind Order Received");
        } else {
          setSelectedStatuses(statusArray);
        }
      } else {
        setSelectedStatuses(["Bound", "Bind Order Received"]);
        localStorage.setItem("currentFilterStatus", "Bound,Bind Order Received");
      }

      // Set hasClaimsOnly
      if (savedHasClaimsOnly === "true") {
        setHasClaimsOnly(true);
      }

      // Load zipcodes
      if (savedZipcodes) {
        const parsedZipcodes = JSON.parse(savedZipcodes);
        if (Array.isArray(parsedZipcodes) && parsedZipcodes.length > 0) {
          console.log("Loaded", parsedZipcodes.length, "zipcodes from localStorage");
          setZipcodes(parsedZipcodes);
        }
      }

      console.log("✅ Initialization complete");
      
    } catch (error) {
      console.error("Error loading saved state from localStorage:", error);
      localStorage.removeItem("currentZipcodes");
    } finally {
      // Mark initialization as complete after a brief delay to ensure state updates
      setTimeout(() => {
        initializationComplete.current = true;
        isInitializing.current = false;
        console.log("🏁 Ready for user interactions");
      }, 100);
    }
  }, []);

  useEffect(() => {
    const isControlNoSearch =
      localStorage.getItem("isControlNoOnlySearch") === "true";
    const controlNo = document.getElementById("ControlNo")?.value?.trim();

    console.log("✅ useEffect fired");
    console.log("➡️ isGeocodingInProgress", isGeocodingInProgress);
    console.log("➡️ addressMarkers.length", addressMarkers.length);
    console.log("➡️ isControlNoOnlySearch", isControlNoSearch);
    console.log("➡️ controlNo", controlNo);

    if (
      !searchOnSelection &&
      !isGeocodingInProgress &&
      addressMarkers.length > 0 &&
      isControlNoSearch &&
      controlNo &&
      !hasTriggeredRef.current
    ) {
      const matchingMarker = insuredsData.find(
        (marker) => marker?.ControlNo?.toLowerCase() === controlNo.toLowerCase()
      );

      console.log("➡️ matchingMarker", matchingMarker);
      if (matchingMarker) {
        handleListItemClick(matchingMarker);
        hasTriggeredRef.current = true; // 🔒 Prevents re-trigger
      }
    }
  }, [addressMarkers, isGeocodingInProgress]);

  // After your existing status-change effect:
  // ===== FIX 21: Consolidated Search Effect with Debouncing =====
  // WHY: Multiple useEffects trigger overlapping searches
  // HOW: Single effect monitors all filter changes with debouncing
  useEffect(() => {
    // ===== FIX: Skip during initialization =====
    if (!initializationComplete.current || isInitializing.current) {
      console.log("⏸️ Skipping search during initialization");
      return;
    }

    // Skip during clearing to prevent unwanted searches
    if (isClearing) {
      console.log("⏸️ Skipping search during clear operation");
      return;
    }

    // Only trigger if we have a map center or controlNo flag set; otherwise,
    // treat it like a fresh search with no location
    const carrierString = Array.isArray(selectedCarriers)
      ? selectedCarriers.join(",")
      : "";
    const controlNo = document.getElementById("ControlNo")?.value || "";
    const statusString = Array.isArray(selectedStatuses)
      ? selectedStatuses.join(",")
      : "";

    // Check if search parameters actually changed
    const paramsChanged = 
      lastSearchParams.current.lat !== (markerPosition?.lat || null) ||
      lastSearchParams.current.lng !== (markerPosition?.lng || null) ||
      lastSearchParams.current.radius !== radius ||
      lastSearchParams.current.carriers !== carrierString ||
      lastSearchParams.current.controlNo !== controlNo ||
      lastSearchParams.current.status !== statusString ||
      lastSearchParams.current.hasClaimsOnly !== hasClaimsOnly;

    if (!paramsChanged) {
      console.log("⏩ Skipping search - parameters unchanged");
      return;
    }

    // Update last search params
    lastSearchParams.current = {
      lat: markerPosition?.lat || null,
      lng: markerPosition?.lng || null,
      radius: radius,
      carriers: carrierString,
      controlNo: controlNo,
      status: statusString,
      hasClaimsOnly: hasClaimsOnly,
    };

    console.log("🔄 Filter change detected, triggering search");

    // If no location and no controlNo, we still want to fetch all insureds
    // for the selected carriers across your entire dataset
    if (!markerPosition && !location && !controlNo) {
      // Treat this as a global carrier filter search:
      fetchZipcodesAndLoadData(
        null, // lat
        null, // lng
        radius, // radius – you might default to 0 or some fallback
        carrierString,
        controlNo, // empty
        statusString,
        hasClaimsOnly
      );
    }
  }, [selectedCarriers, selectedStatuses, markerPosition, radius, hasClaimsOnly, location, isClearing]);

  // ===== REMOVED: Redundant Marker Position Effect =====
  // WHY: Marker position changes are already handled by the consolidated search effect above
  // The consolidated effect monitors markerPosition in its dependencies
  // REMOVED: Duplicate useEffect that was causing race conditions and double searches

  useEffect(() => {
    return () => {
      // Close any open InfoWindow when component unmounts
      if (window.currentInfoWindow) {
        window.currentInfoWindow.close();
        window.currentInfoWindow = null;
      }
    };
  }, []);

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobileView(window.innerWidth < 768);
    };

    checkIsMobile();
    window.addEventListener("resize", checkIsMobile);
    return () => window.removeEventListener("resize", checkIsMobile);
  }, []);

  useEffect(() => {
    if (isClearing) return;

    if (markerPosition || location) {
      const statusString = Array.isArray(selectedStatuses)
        ? selectedStatuses.join(",")
        : "Bound";

      fetchInsuredsData(
        markerPosition?.lat,
        markerPosition?.lng,
        undefined, // zipcodesList - will be handled in the function
        1, // page
        Array.isArray(selectedCarriers) ? selectedCarriers.join(",") : "", // carrierName
        undefined, // controlNo - will be handled in the function
        statusString // statusFilter - FIX: Add this parameter
      );
    }
  }, [isClearing, markerPosition, location, selectedStatuses]);

  useEffect(() => {
    if (zipcodes && zipcodes.length > 0) {
      localStorage.setItem("currentZipcodes", JSON.stringify(zipcodes));
      console.log(
        "Zipcodes saved to localStorage:",
        zipcodes.length,
        "zipcodes"
      );
    }
  }, [zipcodes]);

  const handleStatusChange = (statusValue) => {
    // Make sure we have at least one status selected or use default "Bound"
    const finalStatusValue =
      Array.isArray(statusValue) && statusValue.length > 0
        ? statusValue
        : ["Bound", "Bind Order Received"];

    const statusString = finalStatusValue.join(",");
    localStorage.setItem("currentFilterStatus", statusString);

    // Signal that we're starting a new data load
    window.dispatchEvent(new CustomEvent("geo-data-loading-start"));

    // Clear existing markers and data
    setAddressMarkers([]);
    setInsuredsData([]);

    // Close any open InfoWindow
    if (window.currentInfoWindow) {
      window.currentInfoWindow.close();
      window.currentInfoWindow = null;
    }

    // Reset selected insured
    setSelectedInsured(null);

    // Abort any ongoing geocoding operations
    if (geocodingAbortController.current) {
      geocodingAbortController.current.abort();
      geocodingAbortController.current = null;
    }

    // Reset all in-progress flags to prevent race conditions
    Object.keys(apiCallsInProgress.current).forEach((key) => {
      apiCallsInProgress.current[key] = false;
    });

    const controlNo = document.getElementById("ControlNo")?.value || "";
    const effectiveRadius = (!radius || radius === 0) ? 5 : radius;

    // If we already have active search parameters, use them to refresh the search
    if (markerPosition && zipcodes.length > 0) {
      // Use existing marker position and radius with new status filter
      setIsLoading(true); // Add loading indicator
      fetchZipcodesAndLoadData(
        markerPosition.lat,
        markerPosition.lng,
        effectiveRadius,
        Array.isArray(selectedCarriers) ? selectedCarriers.join(",") : "",
        controlNo,
        statusString,
        hasClaimsOnly // Add hasClaimsOnly parameter
      );
    } else if (controlNo) {
      // If only control number is available, do a control number search
      setIsLoading(true); // Add loading indicator
      fetchZipcodesAndLoadData(
        null,
        null,
        effectiveRadius,
        Array.isArray(selectedCarriers) ? selectedCarriers.join(",") : "",
        controlNo,
        statusString,
        hasClaimsOnly // Add hasClaimsOnly parameter
      );
    } else if (location) {
      // If location is entered but not searched yet, trigger a full search
      handleSearch();
    } else {
      // If no search parameters, just update the filter for next search
      window.dispatchEvent(
        new CustomEvent("geo-data-loading-complete", {
          detail: {
            filters: {
              carrier: Array.isArray(selectedCarriers)
                ? selectedCarriers.join(",")
                : "",
              controlNo: controlNo,
              status: statusString,
              hasClaimsOnly: hasClaimsOnly,
            },
          },
        })
      );
    }
  };

  const handleHasClaimsOnlyChange = () => {
    const newValue = !hasClaimsOnly;
    setHasClaimsOnly(newValue);

    // Save to localStorage
    localStorage.setItem("currentFilterHasClaimsOnly", newValue.toString());

    // Signal that we're starting a new data load
    window.dispatchEvent(new CustomEvent("geo-data-loading-start"));

    // Clear existing markers and data
    setAddressMarkers([]);
    setInsuredsData([]);

    // Close any open InfoWindow
    if (window.currentInfoWindow) {
      window.currentInfoWindow.close();
      window.currentInfoWindow = null;
    }

    // Reset selected insured
    setSelectedInsured(null);

    // Abort any ongoing geocoding operations
    if (geocodingAbortController.current) {
      geocodingAbortController.current.abort();
      geocodingAbortController.current = null;
    }

    // Reset all in-progress flags to prevent race conditions
    Object.keys(apiCallsInProgress.current).forEach((key) => {
      apiCallsInProgress.current[key] = false;
    });

    const controlNo = document.getElementById("ControlNo")?.value || "";
    const effectiveRadius = (!radius || radius === 0) ? 5 : radius;
    const statusString = Array.isArray(selectedStatuses)
      ? selectedStatuses.join(",")
      : "Bound";
    const carriers = Array.isArray(selectedCarriers)
      ? selectedCarriers.join(",")
      : "";

    // If we already have active search parameters, refresh the search with new filter
    if (markerPosition && zipcodes.length > 0) {
      setIsLoading(true);
      fetchZipcodesAndLoadData(
        markerPosition.lat,
        markerPosition.lng,
        effectiveRadius,
        Array.isArray(selectedCarriers) ? selectedCarriers.join(",") : "",
        controlNo,
        statusString,
        newValue // Pass the new hasClaimsOnly value
      );
    } else if (carriers) {
      setIsLoading(true);
      fetchZipcodesAndLoadData(
        null,
        null,
        effectiveRadius,
        Array.isArray(selectedCarriers) ? selectedCarriers.join(",") : "",
        null,
        statusString,
        newValue // Pass the new hasClaimsOnly value
      );
    } else if (controlNo) {
      setIsLoading(true);
      fetchZipcodesAndLoadData(
        null,
        null,
        effectiveRadius,
        Array.isArray(selectedCarriers) ? selectedCarriers.join(",") : "",
        controlNo,
        statusString,
        newValue // Pass the new hasClaimsOnly value
      );
    } else if (location) {
      handleSearch();
    } else {
      // Update the filter for next search
      window.dispatchEvent(
        new CustomEvent("geo-data-loading-complete", {
          detail: {
            filters: {
              carrier: Array.isArray(selectedCarriers)
                ? selectedCarriers.join(",")
                : "",
              controlNo: controlNo,
              status: statusString,
              hasClaimsOnly: newValue,
            },
          },
        })
      );
    }
  };

  const generateZipcodeBoundary = async (
    zipcodesList,
    centerLat,
    centerLng,
    radiusParam,
    controlNo = ""
  ) => {
    // if (!zipcodesList || zipcodesList.length === 0) return;

    if (
      (!zipcodesList || zipcodesList.length === 0) &&
      (centerLat == null || centerLng == null) &&
      !controlNo
    ) {
      return;
    }

    setIsLoadingBoundaries(true);

    // CLEAR ALL EXISTING BOUNDARIES FIRST
    const clearAllBoundaries = () => {
      console.log("🧹 Clearing all existing boundaries...");

      // Clear zipcode outline polygons
      if (window.boundaryPolygons && window.boundaryPolygons.length > 0) {
        console.log(
          `Removing ${window.boundaryPolygons.length} zipcode outline polygons`
        );
        window.boundaryPolygons.forEach((polygon) => polygon.setMap(null));
        window.boundaryPolygons = [];
      }

      // Clear halo circle boundaries from state
      setZipcodeBoundaries([]);
      console.log("✅ All boundaries cleared");
    };

    // Clear everything at the start
    clearAllBoundaries();

    // Function to create circle polygon (for control number searches)
    const createCirclePolygon = (center, radius, numPoints = 32) => {
      const points = [];
      for (let i = 0; i < numPoints; i++) {
        const angle = (i / numPoints) * 360 * (Math.PI / 180);
        const lat = center.lat + (radius / 111111) * Math.cos(angle);
        const lng =
          center.lng +
          (radius / (111111 * Math.cos(center.lat * (Math.PI / 180)))) *
            Math.sin(angle);
        points.push({ lat, lng });
      }
      // Close the polygon
      points.push(points[0]);
      return points;
    };

    // Function to adjust map zoom based on radius
    const adjustMapZoom = (radiusParam) => {
      if (!mapRef.current) return;

      let newZoom = 12;

      if (radiusParam) {
        if (radiusParam <= 1) {
          newZoom = 13;
        } else if (radiusParam <= 5) {
          newZoom = 11;
        } else if (radiusParam <= 20) {
          newZoom = 9;
        } else if (radiusParam <= 50) {
          newZoom = 8;
        } else if (radiusParam <= 100) {
          newZoom = 7;
        } else {
          newZoom = 5;
        }
      } else {
        newZoom = 13;
      }

      setTimeout(() => {
        mapRef.current.setZoom(newZoom);
        setHasZoomedToFit(true);
      }, 800);
    };

    // Helper function to create halo boundary (for control number searches)
    const createHaloBoundary = (lat, lng, radius) => {
      console.log("🎯 Creating HALO boundary at:", lat, lng, "radius:", radius);

      if (!radius || radius === 0) {
        const smallRadius = 5 * 1609.34; // Match the default 5-mile backend search radius
        const circlePoints = createCirclePolygon({ lat, lng }, smallRadius);

        setZipcodeBoundaries([
          {
            paths: circlePoints,
            isSingleZipcode: true,
          },
        ]);
        console.log("✅ Created small halo circle (5 miles)");
      } else {
        const radiusInMeters = radius * 1609.34; // Convert miles to meters
        const circlePoints = createCirclePolygon({ lat, lng }, radiusInMeters);

        setZipcodeBoundaries([
          {
            paths: circlePoints,
            isSearchArea: true,
          },
        ]);
        console.log(`✅ Created search area halo circle (${radius} miles)`);
      }

      adjustMapZoom(radius);
    };

    // NEW: Function to create actual zipcode boundary outlines
    const createZipcodeOutlines = async (zipcodesList) => {
      console.log("🗺️ Creating ZIPCODE OUTLINES for:", zipcodesList);

      // Note: Boundaries already cleared by clearAllBoundaries() at start
      window.boundaryPolygons = window.boundaryPolygons || [];

      const allBoundaries = [];
      const allBounds = new window.google.maps.LatLngBounds();

      // Check cache for all zipcodes first
      const uncachedZipcodes = [];
      const cachedBoundaries = [];

      zipcodesList.forEach((zipcode, index) => {
        if (
          zipcodeBoundaryCache.current &&
          zipcodeBoundaryCache.current[zipcode]
        ) {
          console.log(`Using cached boundary for zipcode: ${zipcode}`);
          cachedBoundaries.push({
            zipcode,
            index,
            boundary: zipcodeBoundaryCache.current[zipcode][0],
          });
        } else {
          uncachedZipcodes.push({ zipcode, index });
        }
      });

      // Process cached boundaries first
      cachedBoundaries.forEach(({ zipcode, index, boundary }) => {
        allBoundaries.push(boundary);
        createPolygonOnMap(boundary, zipcode, index, allBounds);
      });

      // Process uncached zipcodes in batches
      if (uncachedZipcodes.length > 0) {
        const BATCH_SIZE = 5;

        for (let i = 0; i < uncachedZipcodes.length; i += BATCH_SIZE) {
          const batch = uncachedZipcodes.slice(i, i + BATCH_SIZE);
          const batchZipcodes = batch.map((item) => item.zipcode).join(",");

          console.log(`Fetching boundaries for batch: ${batchZipcodes}`);

          try {
            const response = await fetch(
              `https://vanitysoft-boundaries-io-v1.p.rapidapi.com/rest/v1/public/boundary/zipcode?zipcode=${encodeURIComponent(
                batchZipcodes
              )}`,
              {
                headers: {
                  "x-rapidapi-host":
                    "vanitysoft-boundaries-io-v1.p.rapidapi.com",
                  "x-rapidapi-key": rapidApiKey,
                },
              }
            );

            if (response.ok) {
              const boundaryData = await response.json();

              if (
                boundaryData &&
                boundaryData.features &&
                boundaryData.features.length > 0
              ) {
                boundaryData.features.forEach((feature, featureIndex) => {
                  const batchItem = batch[featureIndex];
                  if (!batchItem || !feature.geometry) return;

                  const { zipcode, index } = batchItem;
                  const paths = extractPathsFromGeometry(feature.geometry);

                  if (paths.length > 0) {
                    const boundaryObject = {
                      paths: paths,
                      isSingleZipcode: true,
                      zipcode: zipcode,
                    };

                    // Cache the boundary
                    if (!zipcodeBoundaryCache.current)
                      zipcodeBoundaryCache.current = {};
                    zipcodeBoundaryCache.current[zipcode] = [boundaryObject];

                    allBoundaries.push(boundaryObject);
                    createPolygonOnMap(
                      boundaryObject,
                      zipcode,
                      index,
                      allBounds
                    );
                  }
                });
              }
            } else {
              // Fall back to individual calls
              await processBatchIndividually(batch, allBoundaries, allBounds);
            }
          } catch (error) {
            console.error(`Error fetching batch boundaries:`, error);
            await processBatchIndividually(batch, allBoundaries, allBounds);
          }

          // Add delay between batches
          if (i + BATCH_SIZE < uncachedZipcodes.length) {
            await new Promise((resolve) => setTimeout(resolve, 100));
          }
        }
      }

      // Update state and fit map
      if (allBoundaries.length > 0) {
        setZipcodeBoundaries(allBoundaries);

        if (window.google && window.google.maps && mapRef.current) {
          mapRef.current.fitBounds(allBounds);
          setTimeout(() => setHasZoomedToFit(true), 800);
        }
        console.log(
          `✅ Created ${allBoundaries.length} zipcode outline boundaries`
        );
      }
    };

    // Helper function to extract paths from geometry
    const extractPathsFromGeometry = (geometry) => {
      const coordinates = geometry.coordinates;
      const geometryType = geometry.type;
      let paths = [];

      if (geometryType === "Polygon") {
        paths = coordinates[0].map((coord) => ({
          lat: coord[1],
          lng: coord[0],
        }));
      } else if (geometryType === "MultiPolygon") {
        paths = coordinates[0][0].map((coord) => ({
          lat: coord[1],
          lng: coord[0],
        }));
      }

      return paths;
    };

    // Helper function to create polygon on map
    const createPolygonOnMap = (boundary, zipcode, index, allBounds) => {
      if (window.google && window.google.maps && mapRef.current) {
        const strokeColor = getZipcodeColor(index, "stroke");
        const fillColor = getZipcodeColor(index, "fill");

        const polygon = new window.google.maps.Polygon({
          paths: boundary.paths,
          strokeColor: strokeColor,
          strokeOpacity: 0.8,
          strokeWeight: 2,
          fillColor: fillColor,
          fillOpacity: 0.2,
          map: mapRef.current,
        });

        addZipcodeTooltip(polygon, zipcode);

        window.boundaryPolygons = window.boundaryPolygons || [];
        window.boundaryPolygons.push(polygon);

        boundary.paths.forEach((point) => {
          allBounds.extend(new window.google.maps.LatLng(point.lat, point.lng));
        });
      }
    };

    // Helper function to get colors for different zipcodes
    const getZipcodeColor = (index, type) => {
      // All zipcode outlines will be green
      return "#4CAF50"; // Green for all zipcodes
    };

    // Helper function to add tooltip
    const addZipcodeTooltip = (polygon, zipcode) => {
      const infoWindow = new window.google.maps.InfoWindow();

      window.google.maps.event.addListener(
        polygon,
        "mouseover",
        function (event) {
          infoWindow.setContent(
            `<div style="padding: 5px; font-weight: bold;">Zipcode: ${zipcode}</div>`
          );
          infoWindow.setPosition(event.latLng);
          infoWindow.open(mapRef.current);
        }
      );

      window.google.maps.event.addListener(polygon, "mouseout", function () {
        infoWindow.close();
      });
    };

    // Fallback function for individual API calls
    const processBatchIndividually = async (
      batch,
      allBoundaries,
      allBounds
    ) => {
      for (const { zipcode, index } of batch) {
        try {
          const response = await fetch(
            `https://vanitysoft-boundaries-io-v1.p.rapidapi.com/rest/v1/public/boundary/zipcode?zipcode=${zipcode}`,
            {
              headers: {
                "x-rapidapi-host": "vanitysoft-boundaries-io-v1.p.rapidapi.com",
                "x-rapidapi-key": "",
              },
            }
          );

          if (response.ok) {
            const boundaryData = await response.json();

            if (
              boundaryData &&
              boundaryData.features &&
              boundaryData.features.length > 0 &&
              boundaryData.features[0].geometry
            ) {
              const paths = extractPathsFromGeometry(
                boundaryData.features[0].geometry
              );

              if (paths.length > 0) {
                const boundaryObject = {
                  paths: paths,
                  isSingleZipcode: true,
                  zipcode: zipcode,
                };

                if (!zipcodeBoundaryCache.current)
                  zipcodeBoundaryCache.current = {};
                zipcodeBoundaryCache.current[zipcode] = [boundaryObject];
                allBoundaries.push(boundaryObject);
                createPolygonOnMap(boundaryObject, zipcode, index, allBounds);
              }
            }
          }

          await new Promise((resolve) => setTimeout(resolve, 50));
        } catch (error) {
          console.error(
            `Error fetching individual boundary for ${zipcode}:`,
            error
          );
        }
      }
    };

    // MAIN LOGIC: Decide between zipcode outlines vs halo circles

    console.log("🔍 BOUNDARY DECISION LOGIC:");
    console.log("centerLat:", centerLat);
    console.log("centerLng:", centerLng);
    console.log("controlNo:", controlNo);
    console.log("zipcodesList:", zipcodesList);
    console.log("radiusParam:", radiusParam);

    // For control number searches with no explicit lat/lng provided
    if ((centerLat === null || centerLng === null) && controlNo) {
      console.log(
        "🎯 CASE 1: Control Number search with no coordinates - Using HALO CIRCLE approach"
      );

      const token = localStorage.getItem("accessToken");
      if (!token) {
        setIsLoadingBoundaries(false);
        return;
      }

      try {
        const specificUrl = `${apiUrl}fetch-insureds-listing?status=All&controlNo=${encodeURIComponent(
          controlNo
        )}`;

        const specificResponse = await fetch(specificUrl, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (specificResponse.ok) {
          const result = await specificResponse.json();

          if (result.data && result.data.length > 0) {
            const insuredData = result.data[0];
            const [address] = pickGeocodingAddresses(insuredData.Address1, insuredData.Address2);

            if (address && window.google && window.google.maps) {
              const geocoder = new window.google.maps.Geocoder();

              geocoder.geocode({ address }, (results, status) => {
                if (status === "OK" && results[0]) {
                  const derivedLat = results[0].geometry.location.lat();
                  const derivedLng = results[0].geometry.location.lng();

                  console.log(
                    "✅ Creating HALO BOUNDARY for control number search"
                  );
                  createHaloBoundary(derivedLat, derivedLng, radiusParam);

                  setMapCenter({ lat: derivedLat, lng: derivedLng });
                  setMarkerPosition({ lat: derivedLat, lng: derivedLng });

                  if (mapRef.current) {
                    mapRef.current.panTo({ lat: derivedLat, lng: derivedLng });
                  }
                } else {
                  console.error(
                    "❌ Failed to geocode insured address:",
                    status
                  );
                  console.log("🔄 Falling back to zipcode center method");
                  fallbackToZipcodeCenter();
                }
              });
              return;
            } else {
              console.log(
                "❌ No valid address found for insured, falling back to zipcode"
              );
              fallbackToZipcodeCenter();
            }
          } else {
            console.log("❌ No insured data found, falling back to zipcode");
            fallbackToZipcodeCenter();
          }
        } else {
          console.error(
            "❌ Failed to fetch insured data:",
            specificResponse.status
          );
          console.log("🔄 Falling back to zipcode center method");
          fallbackToZipcodeCenter();
        }
      } catch (error) {
        console.error("❌ Error fetching insured data for boundary:", error);
        console.log("🔄 Falling back to zipcode center method");
        fallbackToZipcodeCenter();
      }
    }
    // For zipcode searches (location-based searches)
    else if (!controlNo && zipcodesList.length > 0) {
      // If we have coordinates and a radius, show a halo circle instead of green zipcode outlines
      if (centerLat !== null && centerLng !== null && radiusParam && radiusParam > 0) {
        console.log(
          "🎯 CASE 2a: Location search with radius - Using HALO CIRCLE"
        );
        createHaloBoundary(centerLat, centerLng, radiusParam);
      } else {
        console.log(
          "🎯 CASE 2b: Pure zipcode search (no radius) - Using ZIPCODE OUTLINES"
        );
        console.log("📍 Zipcodes to process:", zipcodesList);
        await createZipcodeOutlines(zipcodesList);
      }
    }
    // For searches with existing coordinates
    else if (centerLat !== null && centerLng !== null) {
      // If we have controlNo or radius, use halo; otherwise use zipcode outlines
      if (controlNo || (radiusParam && radiusParam > 0)) {
        console.log(
          "🎯 CASE 3: Search WITH coordinates - Using HALO CIRCLE"
        );
        createHaloBoundary(centerLat, centerLng, radiusParam);
      } else {
        console.log(
          "🎯 CASE 4: Location search WITH coordinates (no radius) - Using ZIPCODE OUTLINES"
        );
        await createZipcodeOutlines(zipcodesList);
      }
    } else {
      console.log("🎯 CASE 5: FALLBACK - No valid conditions met");
      console.log("🔄 Going to fallbackToZipcodeCenter()");
      fallbackToZipcodeCenter();
    }

    // Helper function for fallback to zipcode center method
    function fallbackToZipcodeCenter() {
      console.log("🔄 FALLBACK: Using zipcode center geocoding approach");

      if (zipcodesList.length > 0 && window.google && window.google.maps) {
        const firstZipcode = zipcodesList[0];
        console.log("📍 Geocoding first zipcode:", firstZipcode);
        const geocoder = new window.google.maps.Geocoder();

        geocoder.geocode(
          { address: firstZipcode + " USA" },
          (results, status) => {
            if (status === "OK" && results[0]) {
              const derivedLat = results[0].geometry.location.lat();
              const derivedLng = results[0].geometry.location.lng();

              console.log(
                "✅ FALLBACK: Creating HALO BOUNDARY for zipcode center"
              );
              console.log("📍 Coordinates:", derivedLat, derivedLng);
              createHaloBoundary(derivedLat, derivedLng, radiusParam);

              setMapCenter({ lat: derivedLat, lng: derivedLng });
              setMarkerPosition({ lat: derivedLat, lng: derivedLng });

              if (mapRef.current) {
                mapRef.current.panTo({ lat: derivedLat, lng: derivedLng });
              }
            } else {
              console.error("❌ Failed to geocode first zipcode:", status);
              setIsLoadingBoundaries(false);
            }
          }
        );
      } else {
        console.log("❌ No zipcodes available or Google Maps not loaded");
        setIsLoadingBoundaries(false);
      }
    }

    setIsLoadingBoundaries(false);
  };

  const addToast = (message, type = "error") => {
    const id = Date.now(); // Generate unique ID
    setToasts((prev) => [...prev, { id, message, type }]);

    // Optional: Auto remove after 5 seconds
    setTimeout(() => {
      removeToast(id);
    }, 5000);
  };

  // Function to remove a toast
  const removeToast = (id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  };

  // Updated error handler
  useEffect(() => {
    if (error) {
      addToast(error);
      // We don't clear the error here to allow it to be displayed in the UI if needed
    }
  }, [error]);

  const fetchZipcodesAndLoadData = async (
    lat,
    lng,
    radiusParam = null,
    carrierName = "",
    controlNo = "",
    statusFilter = "",
    hasClaimsOnlyFilter = false // Add new parameter
  ) => {
    // ===== FIX 3: Generate Unique Request ID =====
    // WHY: Each search operation needs a unique identifier
    // This allows us to ignore responses from outdated searches
    const requestId = ++currentRequestId.current;
    activeSearchId.current = requestId;
    console.log(`🔍 Starting search with Request ID: ${requestId}`);
    
    // ===== FIX 4: Cancel Previous Requests =====
    // WHY: Prevents multiple overlapping searches from conflicting
    // HOW: Abort all in-flight requests before starting new ones
    Object.keys(abortControllers.current).forEach((key) => {
      if (abortControllers.current[key]) {
        console.log(`❌ Aborting previous ${key} request`);
        abortControllers.current[key].abort();
        abortControllers.current[key] = null;
      }
    });
    
    // ===== FIX 5: Clear Stale Marker Position =====
    // WHY: Old marker positions cause map to show wrong location
    // HOW: Reset marker when starting new search (except for location-based)
    if (!lat && !lng && !controlNo) {
      console.log(`🧹 Clearing stale marker position for carrier-only search`);
      setMarkerPosition(null);
    }
    
    setIsLoading(true);

    // ─ 1) Carrier‑only search: no lat/lng/controlNo but carrier selected.

    if (lat == null && lng == null && !controlNo && carrierName) {
      try {
        // Clear any open info window and markers
        if (window.currentInfoWindow) {
          window.currentInfoWindow.close();
          window.currentInfoWindow = null;
        }
        setAddressMarkers([]);

        // Persist just the carrier filter
        localStorage.setItem("currentFilterCarrier", carrierName);
        localStorage.removeItem("currentFilterControlNo");

        // Notify start of loading
        window.dispatchEvent(new CustomEvent("geo-data-loading-start"));

        // Fire filter‑changed event
        window.dispatchEvent(
          new CustomEvent("filter-params-changed", {
            detail: { carrier: carrierName, controlNo: "", lat, lng },
          })
        );

        // Fetch insureds data without geo constraint
        await fetchInsuredsData(
          null, // no lat
          null, // no lng
          [], // no zipcodes
          1, // page 1
          carrierName,
          undefined,
          statusFilter,
          hasClaimsOnlyFilter,
          requestId // Pass requestId for validation
        );
        await fetchInsuredMarkers(
          [], // no zipcodes
          carrierName,
          undefined,
          statusFilter,
          hasClaimsOnlyFilter,
          requestId // Pass requestId for validation
        );
        // Complete loading
        window.dispatchEvent(
          new CustomEvent("geo-data-loading-complete", {
            detail: {
              filters: {
                carrier: carrierName,
                hasClaimsOnly: hasClaimsOnlyFilter,
              },
            },
          })
        );
      } catch (err) {
        console.error("Carrier‑only load failed:", err);
        window.dispatchEvent(new CustomEvent("geo-data-loading-failed"));
      } finally {
        setIsLoading(false);
      }
      return; // ➔ skip the rest of the function
    }

    // ── 2) Existing zipcode + geo logic continues here ──

    if (window.currentInfoWindow) {
      window.currentInfoWindow.close();
      window.currentInfoWindow = null;
    }

    localStorage.setItem("currentFilterCarrier", carrierName);
    localStorage.setItem(
      "currentFilterHasClaimsOnly",
      hasClaimsOnlyFilter.toString()
    );

    // localStorage.setItem("currentFilterStatus", statusFilter);
    if (lat !== null && lng !== null && controlNo) {
      localStorage.setItem("currentFilterControlNo", controlNo);
    } else {
      // Remove any existing controlNo from localStorage when doing a controlNo-only search
      localStorage.removeItem("currentFilterControlNo");
    }
    localStorage.setItem("currentLatitude", lat);
    localStorage.setItem("currentLongitude", lng);
    localStorage.setItem("radius", radiusParam);
    const ContNo = document.getElementById("ControlNo").value;

    // Signal that we're starting a new data load - this can be used to notify the sidebar
    window.dispatchEvent(new CustomEvent("geo-data-loading-start"));

    // Dispatch filter change event here BEFORE fetching data
    window.dispatchEvent(
      new CustomEvent("filter-params-changed", {
        detail: {
          carrier: carrierName,
          controlNo: controlNo,
          lat: lat,
          lng: lng,
        },
      })
    );

    // Abort any ongoing geocoding operations
    if (geocodingAbortController.current) {
      console.log("Aborting previous geocoding operations");
      geocodingAbortController.current.abort();
      geocodingAbortController.current = null;
    }

    // Reset markers for new search
    setAddressMarkers([]);

    // First, clear the current zipcodes in localStorage to avoid stale data
    localStorage.removeItem("currentZipcodes");
    const isControlNoOnlySearch =
      localStorage.getItem("isControlNoOnlySearch") === "true";

    // Step 1: Fetch zipcodes
    // const fetchedZipcodes = await fetchZipcodes(lat, lng, radiusParam);
    let fetchedZipcodes = [];
    if ((lat !== null && lng !== null) || controlNo) {
      // ===== FIX 16: Pass Request ID to All Fetch Functions =====
      // WHY: Each child function needs to validate its response
      // HOW: Pass the same requestId to all fetch operations
      fetchedZipcodes = await fetchZipcodes(
        lat,
        lng,
        radiusParam,
        lat === null && lng === null ? controlNo : undefined,
        requestId // Pass requestId for validation
      );
    } else {
      console.log(
        "Skipping fetchZipcodes call due to missing lat/lng and controlNo"
      );
    }

    if (fetchedZipcodes && fetchedZipcodes.length > 0) {
      // Update zipcodes state
      setZipcodes(fetchedZipcodes);

      // Save zipcodes to localStorage AFTER we have them, not before
      localStorage.setItem("currentZipcodes", JSON.stringify(fetchedZipcodes));
      console.log(
        "Zipcodes saved to localStorage:",
        fetchedZipcodes.length,
        "zipcodes"
      );

      generateZipcodeBoundary(fetchedZipcodes, lat, lng, radiusParam, ContNo);

      // Signal that zipcode data is now available
      window.dispatchEvent(
        new CustomEvent("zipcodes-updated", {
          detail: { count: fetchedZipcodes.length },
        })
      );

      await fetchInsuredsData(
        lat,
        lng,
        fetchedZipcodes,
        1,
        carrierName,
        !isControlNoOnlySearch ? controlNo : undefined,
        statusFilter,
        hasClaimsOnlyFilter, // Add parameter
        requestId // Pass requestId for validation
      );

      // Fetch map markers with hasClaimsOnly filter
      await fetchInsuredMarkers(
        fetchedZipcodes,
        carrierName,
        !isControlNoOnlySearch ? controlNo : undefined,
        statusFilter,
        hasClaimsOnlyFilter, // Add parameter
        requestId // Pass requestId for validation
      );

      // Step 4: Fetch available carriers for filters - only once
      try {
        setIsCarrierDataLoading(true);
        await fetchCarriersFilters(fetchedZipcodes);
        setIsCarrierDataLoading(false);
      } catch (err) {
        console.error("Error loading carrier filters:", err);
        setIsCarrierDataLoading(false);
      }

      window.dispatchEvent(
        new CustomEvent("geo-data-loading-complete", {
          detail: {
            filters: {
              carrier: carrierName,
              controlNo: controlNo,
              hasClaimsOnly: hasClaimsOnlyFilter,
            },
          },
        })
      );
    } else {
      setIsLoading(false);
      setInsuredsData([]);
      setAddressMarkers([]);
      localStorage.removeItem("currentZipcodes");
      setZipcodes([]);
      // if (!error && !isClearing) {
      //   setError(
      //     "No locations found in this area. Try adjusting your search parameters."
      //   );
      // }

      // Signal that data loading failed
      window.dispatchEvent(new CustomEvent("geo-data-loading-failed"));
    }
  };

  const handleClear = () => {
    console.log("🧹 CLEARING ALL DATA - Full Reset");
    
    setSelectedStatuses(["Bound", "Bind Order Received"]); // Updated default
    localStorage.setItem("currentFilterStatus", "Bound,Bind Order Received");
    setSearchOnSelection(false);
    setHasZoomedToFit(false);
    setHasClaimsOnly(false); // Reset hasClaimsOnly

    setIsClearing(true);
    setIsDropdownOpen(false);

    hasTriggeredRef.current = false;
    // In your handleClear function, add:
    setZipcodeBoundaries([]);

    // Signal that we're clearing data
    window.dispatchEvent(new CustomEvent("geo-data-clearing"));

    // ===== FIX 17: Abort All In-Flight Requests =====
    // WHY: Clear should stop all ongoing operations immediately
    // HOW: Abort all active abort controllers
    Object.keys(abortControllers.current).forEach((key) => {
      if (abortControllers.current[key]) {
        console.log(`❌ Aborting ${key} request during clear`);
        abortControllers.current[key].abort();
        abortControllers.current[key] = null;
      }
    });

    // Clear filter values from localStorage
    localStorage.removeItem("currentFilterCarrier");
    localStorage.removeItem("currentFilterControlNo");
    localStorage.removeItem("currentLatitude");
    localStorage.removeItem("currentLongitude");
    localStorage.removeItem("radius");
    localStorage.removeItem("isControlNoOnlySearch");
    localStorage.removeItem("specificControlNoRecord");

    dispatchFilterChangeEvent("", "");

    // Abort any ongoing geocoding operations
    if (geocodingAbortController.current) {
      geocodingAbortController.current.abort();
      geocodingAbortController.current = null;
    }

    // ===== FIX 18: Reset Request Tracking =====
    // WHY: Clear should invalidate all previous searches
    // HOW: Increment request ID so old responses are discarded
    currentRequestId.current++;
    activeSearchId.current = currentRequestId.current;
    console.log(`🔄 Reset to Request ID: ${activeSearchId.current}`);

    // Reset all in-progress flags
    Object.keys(apiCallsInProgress.current).forEach((key) => {
      apiCallsInProgress.current[key] = false;
    });

    if (document.getElementById("ControlNo")) {
      document.getElementById("ControlNo").value = "";
    }

    setLocation("");
    setRadius(5);
    setSpecificControlNoRecord(null);
    
    // ===== FIX 19: Properly Reset Map State =====
    // WHY: Stale markerPosition causes map to show wrong location
    // HOW: Reset all map-related state to defaults
    console.log("🗺️ Resetting map center and marker position");
    setMapCenter(defaultCenter);
    setMarkerPosition(null); // Critical: Clear the marker
    
    setInsuredsData([]);
    setTotalInsuredsCount(0);
    setAddressMarkers([]);
    setSelectedInsured(null);
    setZipcodes([]);
    setCurrentPage(1);
    setHasMoreData(true);
    setActiveMarkerId(null);
    setBottomPanelOpen(false);
    setSelectedCarriers([]);
    setSelectedStatus("");
    setSelectedControlNo(null);
    // setAvailableCarriers([]);
    setError(null);

    // Remove zipcodes from localStorage - do this LAST to ensure proper coordination
    localStorage.removeItem("currentZipcodes");

    // Close any open InfoWindow
    if (window.currentInfoWindow) {
      window.currentInfoWindow.close();
      window.currentInfoWindow = null;
    }

    if (mapRef.current) {
      mapRef.current.panTo(defaultCenter);
      mapRef.current.setZoom(10);
    }

    if (isMobileView) {
      setIsOpen(false);
    }

    console.log("All settings have been reset to default");

    // Signal that clearing is complete
    window.dispatchEvent(new CustomEvent("geo-data-cleared"));

    setTimeout(() => setIsClearing(false), 500);
  };

  // const fetchCarriersFilters = async (zipcodesList) => {
  //   const token = localStorage.getItem("accessToken");
  //   if (!token || !zipcodesList || zipcodesList.length === 0) return;

  //   // Track this API call
  //   apiCallsInProgress.current.carriers = true;

  //   // Combine zipcodes into comma-separated string
  //   const zipcodesParam = zipcodesList.join(",");

  //   // Set up retry logic
  //   let retries = 0;
  //   const maxRetries = 3;
  //   const baseDelay = 2000;

  //   while (retries < maxRetries) {
  //     try {
  //       // Set up timeout handling
  //       const controller = new AbortController();
  //       const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

  //       const response = await fetch(
  //         `${apiUrl}fetch-carriers-filters?zipcodes=${zipcodesParam}`,
  //         {
  //           method: "GET",
  //           headers: {
  //             Authorization: `Bearer ${token}`,
  //             "Content-Type": "application/json",
  //           },
  //           signal: controller.signal,
  //         }
  //       );

  //       // Clear the timeout once response is received
  //       clearTimeout(timeoutId);

  //       if (response.ok) {
  //         const result = await response.json();
  //         console.log("Carriers data received:", result);

  //         if (result.data && Array.isArray(result.data)) {
  //           setAvailableCarriers(result.data);

  //           // Signal that carrier data is available
  //           window.dispatchEvent(
  //             new CustomEvent("carrier-data-updated", {
  //               detail: { carriers: result.data.length },
  //             })
  //           );

  //           apiCallsInProgress.current.carriers = false;
  //           return result;
  //         }
  //       } else {
  //         console.error("Failed to fetch carriers:", response.status);
  //         throw new Error(`Failed to fetch carriers: ${response.status}`);
  //       }
  //     } catch (error) {
  //       console.error(`Attempt ${retries + 1} failed:`, error);

  //       if (retries === maxRetries - 1) {
  //         apiCallsInProgress.current.carriers = false;
  //         addToast(`Error loading carriers: ${error.message}`);
  //         throw error;
  //       }

  //       // Otherwise wait with exponential backoff and try again
  //       const delay = baseDelay * Math.pow(2, retries);
  //       await new Promise((resolve) => setTimeout(resolve, delay));
  //       retries++;
  //     }
  //   }

  //   apiCallsInProgress.current.carriers = false;
  // };

  const fetchCarriersFilters = async () => {
    const token = localStorage.getItem("accessToken");
    if (!token) return;

    apiCallsInProgress.current.carriers = true;

    let retries = 0;
    const maxRetries = 3;
    const baseDelay = 2000;

    while (retries < maxRetries) {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 30000); // 30s timeout

        const response = await fetch(`${apiUrl}fetch-carriers-filters`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        if (response.ok) {
          const result = await response.json();
          console.log("Carriers data received:", result);

          if (result.data && Array.isArray(result.data)) {
            setAvailableCarriers(result.data);

            window.dispatchEvent(
              new CustomEvent("carrier-data-updated", {
                detail: { carriers: result.data.length },
              })
            );

            apiCallsInProgress.current.carriers = false;
            return result;
          }
        } else {
          console.error("Failed to fetch carriers:", response.status);
          throw new Error(`Failed to fetch carriers: ${response.status}`);
        }
      } catch (error) {
        console.error(`Attempt ${retries + 1} failed:`, error);

        if (retries === maxRetries - 1) {
          apiCallsInProgress.current.carriers = false;
          addToast(`Error loading carriers: ${error.message}`);
          throw error;
        }

        const delay = baseDelay * Math.pow(2, retries);
        await new Promise((resolve) => setTimeout(resolve, delay));
        retries++;
      }
    }

    apiCallsInProgress.current.carriers = false;
  };

  const fetchStatuses = async () => {
    const token = localStorage.getItem("accessToken");
    if (!token) return;

    let retries = 0;
    const maxRetries = 3;
    const baseDelay = 2000;

    while (retries < maxRetries) {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 30000);

        const response = await fetch(`${apiUrl}fetch-statuses`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        if (response.ok) {
          const result = await response.json();
          const statuses = result?.statuses;
          if (Array.isArray(statuses)) {
            const resolvedStatuses = statuses
              .map((status) => status.description)
              .filter(Boolean)
              .sort((a, b) => a.localeCompare(b));
            setAvailableStatuses(resolvedStatuses);
            setSelectedStatuses((prev) => {
              if (resolvedStatuses.length === 0) {
                return prev;
              }
              const filtered = prev.filter((status) =>
                resolvedStatuses.includes(status)
              );
              const defaults = ["Bound", "Bind Order Received"].filter(
                (status) => resolvedStatuses.includes(status)
              );
              const next = filtered.length > 0 ? filtered : defaults;
              const finalList = next.length > 0 ? next : [resolvedStatuses[0]];
              localStorage.setItem(
                "currentFilterStatus",
                finalList.join(",")
              );
              return finalList;
            });
            return result;
          }
        } else {
          throw new Error(`Failed to fetch statuses: ${response.status}`);
        }
      } catch (error) {
        if (retries === maxRetries - 1) {
          addToast(`Error loading statuses: ${error.message}`);
          throw error;
        }

        const delay = baseDelay * Math.pow(2, retries);
        await new Promise((resolve) => setTimeout(resolve, delay));
        retries++;
      }
    }
  };

  useEffect(() => {
    fetchCarriersFilters().catch((err) =>
      console.error("Error loading carriers on mount:", err)
    );
    fetchStatuses().catch((err) =>
      console.error("Error loading statuses on mount:", err)
    );
  }, []);

  // Modified fetchZipcodes function to handle controlNo + radius scenario
  const fetchZipcodes = async (
    lat,
    lng,
    radiusParam = null,
    controlNo = "",
    requestId = null // Add requestId parameter for validation
  ) => {
    const token = localStorage.getItem("accessToken");

    if (!token) {
      const errorMsg = "Authentication error. Please log in again.";
      setError(errorMsg);
      addToast(errorMsg);
      return null;
    }

    // ===== FIX 6: Abort Previous Zipcode Requests =====
    // WHY: Multiple rapid searches can cause race conditions
    // HOW: Cancel the previous zipcode fetch before starting new one
    if (abortControllers.current.zipcodes) {
      console.log("❌ Aborting previous zipcode fetch");
      abortControllers.current.zipcodes.abort();
    }
    
    // Create new abort controller for this request
    abortControllers.current.zipcodes = new AbortController();
    const signal = abortControllers.current.zipcodes.signal;

    if (apiCallsInProgress.current.zipcodes) {
      console.log(
        "Zipcode fetch already in progress, skipping duplicate request"
      );
      return null;
    }

    apiCallsInProgress.current.zipcodes = true;

    console.log(
      "Fetching zipcodes for location:",
      lat,
      lng,
      radiusParam ? `with radius: ${radiusParam}` : "",
      controlNo ? `and control number: ${controlNo}` : "",
      requestId ? `Request ID: ${requestId}` : ""
    );

    // Build the URL with parameters
    let url = `${apiUrl}fetch-insureds-zipcodes?`;

    // Add lat/long if available
    if (lat && lng) {
      url += `lat=${lat}&long=${lng}`;
    }

    // Add radius if provided
    if (radiusParam !== null && radiusParam > 0) {
      url += `&radius=${radiusParam}`;
    }

    // Add controlNo if provided
    if (controlNo) {
      url += `&controlNo=${encodeURIComponent(controlNo)}`;
    }

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        signal: signal, // Add abort signal
      });

      // ===== FIX 7: Validate Request ID Before Processing =====
      // WHY: Prevents stale responses from overwriting current data
      // HOW: Check if this response belongs to the current active search
      if (requestId && requestId !== activeSearchId.current) {
        console.log(
          `⚠️ Discarding stale zipcode response. Request ID: ${requestId}, Current: ${activeSearchId.current}`
        );
        return null;
      }

      if (response.ok) {
        const result = await response.json();
        console.log("Zipcodes received:", result);
        return result.zipcodes || [];
      } else {
        const errorText = await response.text();
        const errorMsg = `Error fetching zipcodes: ${response.status}`;
        console.error(errorMsg, errorText);
        setError(errorMsg);
        addToast(errorMsg);
        return [];
      }
    } catch (error) {
      // ===== FIX 8: Handle Abort Errors Gracefully =====
      // WHY: Aborted requests shouldn't show as errors to users
      // HOW: Check if error is from abort, log but don't alert user
      if (error.name === "AbortError") {
        console.log("🚫 Zipcode fetch was aborted (new search initiated)");
        return null;
      }
      
      const errorMsg = `Network error: ${error.message}`;
      console.error("Error fetching zipcodes:", error);
      setError(errorMsg);
      addToast(errorMsg);
      return [];
    } finally {
      apiCallsInProgress.current.zipcodes = false;
      abortControllers.current.zipcodes = null;
    }
  };

  const fetchInsuredMarkers = async (
    zipcodesList,
    carrierName = "",
    controlNo = "",
    statusFilter = "",
    hasClaimsOnlyFilter = false,
    requestId = null // Add requestId for validation
  ) => {
    const token = localStorage.getItem("accessToken");
    if (
      !token ||
      ((!zipcodesList || zipcodesList.length === 0) && !carrierName)
    ) {
      setIsLoading(false);
      return;
    }

    // ===== FIX 9: Abort Previous Marker Requests =====
    // WHY: Old marker fetches can return after new ones, showing wrong markers
    // HOW: Cancel previous marker fetch and geocoding before starting new
    if (abortControllers.current.markers) {
      console.log("❌ Aborting previous marker fetch operation");
      abortControllers.current.markers.abort();
    }
    
    // Also abort geocoding if in progress
    if (geocodingAbortController.current) {
      console.log("❌ Aborting previous geocoding operation");
      geocodingAbortController.current.abort();
      geocodingAbortController.current = null;
    }
    
    // Create new abort controller
    abortControllers.current.markers = new AbortController();
    const signal = abortControllers.current.markers.signal;

    apiCallsInProgress.current.markers = true;
    setIsGeocodingInProgress(true);
    setError(null);

    // Create new abort controller for this operation
    geocodingAbortController.current = new AbortController();
    const geocodingSignal = geocodingAbortController.current.signal;

    const zipcodesParam = zipcodesList.join(",");
    let url = `${apiUrl}fetch-insureds-markers?zipcodes=${zipcodesParam}`;

    if (carrierName) {
      url += `&carrierName=${encodeURIComponent(carrierName)}`;
    }

    if (controlNo) {
      url += `&controlNo=${encodeURIComponent(controlNo)}`;
    }

    if (statusFilter) {
      url += `&status=${encodeURIComponent(statusFilter)}`;
    }

    if (hasClaimsOnlyFilter) {
      url += `&hasClaimsOnly=true`;
    }

    console.log(`📍 Fetching markers with Request ID: ${requestId}`);

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        signal: signal, // Use abort signal
      });

      // ===== FIX 10: Validate Request Before Processing Markers =====
      // WHY: Stale marker data can appear on map from old searches
      // HOW: Check request ID before geocoding addresses
      if (requestId && requestId !== activeSearchId.current) {
        console.log(
          `⚠️ Discarding stale marker response. Request ID: ${requestId}, Current: ${activeSearchId.current}`
        );
        setIsGeocodingInProgress(false);
        apiCallsInProgress.current.markers = false;
        return;
      }

      if (response.ok) {
        const result = await response.json();
        console.log("Markers data received:", result);

        if (result.markerAddresses && Array.isArray(result.markerAddresses)) {
          // Pass requestId to geocoding for validation
          geocodeAddresses(result.markerAddresses, geocodingSignal, requestId);
        } else {
          setIsGeocodingInProgress(false);
          apiCallsInProgress.current.markers = false;
        }
      } else {
        const errorText = await response.text();
        const errorMsg = `Error fetching markers: ${response.status}`;
        console.error(errorMsg, errorText);
        setError(errorMsg);
        addToast(errorMsg);
        setIsGeocodingInProgress(false);
        apiCallsInProgress.current.markers = false;
      }
    } catch (error) {
      if (error.name === "AbortError") {
        console.log("🚫 Fetch markers operation aborted (new search initiated)");
      } else {
        const errorMsg = `Network error: ${error.message}`;
        console.error("Error fetching markers:", error);
        setError(errorMsg);
        addToast(errorMsg);
        setIsGeocodingInProgress(false);
        apiCallsInProgress.current.markers = false;
      }
    } finally {
      abortControllers.current.markers = null;
    }
  };

  /**
   * Determines if an address string looks like a mailing/PO Box address
   * rather than a physical property location.
   */
  const isMailingAddress = (address) => {
    if (!address) return false;
    const mailingPatterns = /\b(P\.?O\.?\s*Box|PMB|Mail|Suite\s+\d|Ste\.?\s*\d|c\/o|Attn)/i;
    return mailingPatterns.test(address);
  };

  /**
   * Picks the best address for geocoding — prefers property/physical over mailing.
   * Returns [primary, fallback] addresses to try in order.
   */
  const pickGeocodingAddresses = (address1, address2) => {
    const addr1IsMailing = isMailingAddress(address1);
    const addr2IsMailing = isMailingAddress(address2);

    if (address1 && address2) {
      if (!addr2IsMailing) return [address2, address1];
      if (!addr1IsMailing) return [address1, address2];
      return [address2, address1];
    }

    return [address2 || address1, null];
  };

  const geocodeAddresses = async (addresses, signal, requestId = null) => {
    if (!window.google || !addresses || addresses.length === 0) {
      setIsGeocodingInProgress(false);
      apiCallsInProgress.current.markers = false;

      // Dispatch event to indicate no markers to process
      window.dispatchEvent(
        new CustomEvent("markers-fully-loaded", {
          detail: { count: 0 },
        })
      );

      return;
    }

    // ===== FIX 11: Clear Stale Markers Before Geocoding =====
    // WHY: Old markers should not persist during new searches
    // HOW: Clear marker state at the start of geocoding
    console.log(`🧹 Clearing markers for Request ID: ${requestId}`);
    setAddressMarkers([]);

    const geocoder = new window.google.maps.Geocoder();
    const markers = [];
    const batchSize = 5;
    const batches = Math.ceil(addresses.length / batchSize);

    // Add counters for better tracking
    let totalAddressesToGeocode = addresses.length;
    let successfulGeocodes = 0;
    let failedGeocodes = 0;
    let attemptedGeocodes = 0;

    console.log(`Starting geocoding for ${addresses.length} insureds`);

    // Create a better offset function to distribute markers in a spiral pattern
    const createOffsetPosition = (basePosition, index) => {
      // Create a spiral pattern to distribute markers more visibly
      const angle = (index % 8) * (Math.PI / 4); // 8 directions
      const distance = 0.0002 * (Math.floor(index / 8) + 1); // Increasing distance

      return {
        lat: basePosition.lat + Math.sin(angle) * distance,
        lng: basePosition.lng + Math.cos(angle) * distance,
      };
    };

    try {
      for (let batchIndex = 0; batchIndex < batches; batchIndex++) {
        // Check if the operation was aborted
        if (signal?.aborted) {
          throw new DOMException("Geocoding aborted", "AbortError");
        }

        const batchStart = batchIndex * batchSize;
        const batchEnd = Math.min(
          (batchIndex + 1) * batchSize,
          addresses.length
        );
        const batchAddresses = addresses.slice(batchStart, batchEnd);

        console.log(
          `Processing batch ${batchIndex + 1}/${batches} with ${
            batchAddresses.length
          } addresses`
        );

        // Process each address in the batch - revised to avoid ESLint warning
        const batchPromises = batchAddresses.map((address, index) => {
          const processAddress = async () => {
            if (signal?.aborted) {
              return null;
            }

            const markerId =
              address.ControlNo ||
              `marker-${Math.random().toString(36).substr(2, 9)}`;

            const [primaryAddress, fallbackAddress] = pickGeocodingAddresses(
              address.Address1,
              address.Address2
            );

            // Global index for better offset distribution across batches
            const globalIndex = batchStart + index;

            let result = null;
            let successfulAddress = null;

            // Try primary address first (the one most likely to be a physical location)
            if (primaryAddress) {
              try {
                result = await geocodeAddress(geocoder, primaryAddress);
                if (result) {
                  successfulAddress = primaryAddress;
                  const offsetPosition = createOffsetPosition(result, globalIndex);
                  return {
                    lat: offsetPosition.lat,
                    lng: offsetPosition.lng,
                    insuredData: {
                      ...address,
                      successfulAddress: primaryAddress,
                    },
                    id: markerId,
                    status: { attempted: 1, succeeded: 1, failed: 0 },
                  };
                }
              } catch (error) {
                if (error.name === "AbortError") return null;
                console.error("Error geocoding primary address:", error);
              }
            }

            // Try fallback address
            if (fallbackAddress && !successfulAddress) {
              try {
                result = await geocodeAddress(geocoder, fallbackAddress);
                if (result) {
                  successfulAddress = fallbackAddress;
                  const offsetPosition = createOffsetPosition(result, globalIndex);
                  return {
                    lat: offsetPosition.lat,
                    lng: offsetPosition.lng,
                    insuredData: {
                      ...address,
                      successfulAddress: fallbackAddress,
                    },
                    id: markerId,
                    status: {
                      attempted: primaryAddress ? 2 : 1,
                      succeeded: 1,
                      failed: primaryAddress ? 1 : 0,
                    },
                  };
                }
              } catch (error) {
                if (error.name === "AbortError") return null;
                console.error("Error geocoding fallback address:", error);
              }
            }

            // Try with zipcode as last resort (unchanged)
            const zipMatch1 = address.Address1?.match(/\b\d{5}(?:-\d{4})?\b/);
            const zipMatch2 = address.Address2?.match(/\b\d{5}(?:-\d{4})?\b/);
            const zipCode = zipMatch2?.[0] || zipMatch1?.[0]; // Prioritize zipcode from address2

            if (zipCode && !successfulAddress) {
              try {
                result = await geocodeAddress(geocoder, zipCode);
                if (result) {
                  // Calculate better offset based on global index
                  const offsetPosition = createOffsetPosition(
                    result,
                    globalIndex
                  );
                  return {
                    lat: offsetPosition.lat,
                    lng: offsetPosition.lng,
                    insuredData: {
                      ...address,
                      successfulAddress: `Approximate location for ${zipCode}`,
                    },
                    id: markerId,
                    status: {
                      attempted: (primaryAddress ? 1 : 0) + (fallbackAddress ? 1 : 0) + 1,
                      succeeded: 1,
                      failed: (primaryAddress ? 1 : 0) + (fallbackAddress ? 1 : 0),
                    },
                  };
                }
              } catch (error) {
                if (error.name === "AbortError") {
                  return null;
                }
                console.error("Error geocoding ZipCode:", error);
              }
            }

            // If we got here, we couldn't geocode any address for this insured
            console.warn("Could not geocode any address for:", address);
            return {
              id: markerId,
              status: {
                attempted:
                  (primaryAddress ? 1 : 0) + (fallbackAddress ? 1 : 0) + (zipCode ? 1 : 0),
                succeeded: 0,
                failed:
                  (primaryAddress ? 1 : 0) + (fallbackAddress ? 1 : 0) + (zipCode ? 1 : 0),
              },
              isFailure: true,
            };
          };

          return processAddress();
        });

        // Check for abort before waiting for batch
        if (signal?.aborted) {
          throw new DOMException("Geocoding aborted", "AbortError");
        }

        // Wait for all addresses in this batch to complete
        const batchResults = await Promise.all(batchPromises);

        // ===== FIX 12: Validate Request ID After Each Batch =====
        // WHY: Long geocoding can be outdated by the time it completes
        // HOW: Check request ID after each batch to stop if search changed
        if (requestId && requestId !== activeSearchId.current) {
          console.log(
            `⚠️ Stopping geocoding - request outdated. Request ID: ${requestId}, Current: ${activeSearchId.current}`
          );
          throw new DOMException("Request outdated", "AbortError");
        }

        // Separately track valid markers and statistics
        const validMarkers = [];
        let batchAttempts = 0;
        let batchSuccesses = 0;
        let batchFailures = 0;

        batchResults.forEach((result) => {
          if (result !== null) {
            // Update statistics
            if (result.status) {
              batchAttempts += result.status.attempted;
              batchSuccesses += result.status.succeeded;
              batchFailures += result.status.failed;
            }

            // Only add to markers if it's a successful geocode (not just status tracking)
            if (!result.isFailure) {
              validMarkers.push(result);
            }
          }
        });

        // Update running totals
        attemptedGeocodes += batchAttempts;
        successfulGeocodes += batchSuccesses;
        failedGeocodes += batchFailures;

        // Add valid markers to the collection
        markers.push(...validMarkers);

        // Update markers incrementally
        if (validMarkers.length > 0) {
          setAddressMarkers((prevMarkers) => {
            // Check for duplicates by ID before adding
            const newMarkers = [...prevMarkers];
            const existingIds = new Set(prevMarkers.map((m) => m.id));

            validMarkers.forEach((newMarker) => {
              if (!existingIds.has(newMarker.id)) {
                newMarkers.push(newMarker);
              }
            });

            return newMarkers;
          });
        }

        // Update geocoding stats UI
        setGeocodingStats({
          totalInsureds: totalAddressesToGeocode,
          attempts: attemptedGeocodes,
          successes: successfulGeocodes,
          failures: failedGeocodes,
        });

        // Log progress for this batch
        console.log(`Batch ${batchIndex + 1} complete. Statistics:
      - Addresses in batch: ${batchAddresses.length}
      - Geocoding attempts: ${batchAttempts}
      - Successful geocodes: ${batchSuccesses}
      - Failed geocodes: ${batchFailures}
      - Valid markers created: ${validMarkers.length}`);

        // Add delay between batches to avoid rate limiting
        if (batchIndex < batches - 1) {
          await new Promise((resolve) => {
            // Create a timeout that can be cleared if aborted
            const timeoutId = setTimeout(resolve, 1000);
            if (signal) {
              signal.addEventListener(
                "abort",
                () => {
                  clearTimeout(timeoutId);
                  resolve();
                },
                { once: true }
              );
            }
          });
        }
      }

      console.log(`Geocoding complete. Summary:
    - Total insureds: ${totalAddressesToGeocode}
    - Total geocoding attempts: ${attemptedGeocodes}
    - Successful geocodes: ${successfulGeocodes}
    - Failed geocodes: ${failedGeocodes}
    - Markers created: ${markers.length}`);

      // IMPORTANT NEW CODE: Dispatch custom event when markers are fully loaded
      window.dispatchEvent(
        new CustomEvent("markers-fully-loaded", {
          detail: {
            count: markers.length,
            totalAttempted: totalAddressesToGeocode,
            successful: successfulGeocodes,
            failed: failedGeocodes,
          },
        })
      );
    } catch (error) {
      if (error.name === "AbortError") {
        console.log("Geocoding operation aborted");
        // Keep the existing markers if operation was aborted
      } else {
        console.error("Error during geocoding:", error);
        setError(`Geocoding error: ${error.message}`);
      }

      // Even if there was an error, dispatch the event to unblock any waiting components
      window.dispatchEvent(
        new CustomEvent("markers-fully-loaded", {
          detail: {
            count: markers.length,
            error: error.message || "Unknown error occurred during geocoding",
          },
        })
      );
    } finally {
      setIsGeocodingInProgress(false);
      apiCallsInProgress.current.markers = false;
    }
  };

  const fetchInsuredsData = async (
    lat,
    lng,
    zipcodesList,
    page = 1,
    carrierName = "",
    controlNo = "",
    statusFilter = "",
    hasClaimsOnlyFilter = false,
    requestId = null // Add requestId for validation
  ) => {
    const token = localStorage.getItem("accessToken");
    if (
      !token ||
      ((!zipcodesList || zipcodesList.length === 0) && !carrierName)
    ) {
      setIsLoading(false);
      setIsLoadingMore(false);
      return;
    }

    // ===== FIX 13: Abort Previous Listing Requests =====
    // WHY: Multiple searches cause listings to show wrong data
    // HOW: Cancel previous listing fetch before starting new one
    if (page === 1 && abortControllers.current.listings) {
      console.log("❌ Aborting previous listings fetch");
      abortControllers.current.listings.abort();
    }
    
    // Create new abort controller for page 1
    if (page === 1) {
      abortControllers.current.listings = new AbortController();
    }
    const signal = abortControllers.current.listings?.signal;

    apiCallsInProgress.current.listings = true;
    setError(null);

    if (page === 1) {
      setInsuredsData([]);
      setIsLoading(true);
      // Reset the specific controlNo record when starting a new search
      localStorage.removeItem("specificControlNoRecord");
      setSpecificControlNoRecord(null);
    } else {
      setIsLoadingMore(true);
    }

    // Get the search control number from the input field
    const searchControlNo =
      document.getElementById("ControlNo")?.value?.trim() || controlNo;

    console.log("Starting search with:", {
      searchControlNo,
      page,
      isFirstPage: page === 1,
    });

    // Always attempt to fetch the specific record if we have a control number
    // This ensures it's available for all pages
    let specificRecord = null;

    if (searchControlNo && page === 1) {
      try {
        // Honor the user's active status filter when pinning the
        // specific record. Otherwise a record that doesn't match the filter
        // (e.g. a Renewal-Submitted record when user selected Bound) gets pinned
        // even though the radius search correctly excludes it.
        const specificStatus = statusFilter || "All";
        const specificUrl = `${apiUrl}fetch-insureds-listing?status=${encodeURIComponent(
          specificStatus
        )}&controlNo=${encodeURIComponent(searchControlNo)}`;

        const specificResponse = await fetch(specificUrl, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (specificResponse.ok) {
          const result = await specificResponse.json();
          console.log("Specific controlNo API response:", result);

          if (result.data && result.data.length > 0) {
            // Store the specific record in both state and localStorage
            specificRecord = result.data[0];
            console.log("Found exact control number match:", specificRecord);

            // Store in localStorage for immediate access
            localStorage.setItem(
              "specificControlNoRecord",
              JSON.stringify(specificRecord)
            );

            // Also update state for component rendering
            setSpecificControlNoRecord(specificRecord);
          } else {
            console.log("No exact match found for controlNo:", searchControlNo);
          }
        } else {
          console.log(
            "Error response from specific controlNo endpoint:",
            specificResponse.status
          );
        }
      } catch (error) {
        console.error("Error fetching specific control number:", error);
      }
    } else if (searchControlNo) {
      // For subsequent pages, retrieve the specific record from localStorage
      const storedRecord = localStorage.getItem("specificControlNoRecord");
      if (storedRecord) {
        specificRecord = JSON.parse(storedRecord);
        console.log(
          "Retrieved specific record from localStorage:",
          specificRecord
        );
      }
    }

    // Fetch the regular paginated results
    const zipcodesParam = zipcodesList.join(",");
    let url = `${apiUrl}fetch-insureds-listing?lat=${lat}&long=${lng}&page=${page}&zipcodes=${zipcodesParam}`;

    if (carrierName) {
      url += `&carrierName=${encodeURIComponent(carrierName)}`;
    }

    // Do NOT pass controlNo to the radius listing call. When both controlNo and zipcodes are passed, the backend used to narrow to only that
    // one record — hiding the bound sibling of the same insured. controlNo is already used upstream to seed the radius center; the regular listing just
    // needs to return everything in the zipcodes with status filter + dedup.

    if (statusFilter) {
      url += `&status=${encodeURIComponent(statusFilter)}`;
    }

    if (hasClaimsOnlyFilter) {
      url += `&hasClaimsOnly=true`;
    }

    console.log(`📋 Fetching insureds data (page ${page}) with Request ID: ${requestId}`);

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        signal: signal, // Add abort signal
      });

      // ===== FIX 14: Validate Request Before Processing Listings =====
      // WHY: Stale listing data overwrites current search results
      // HOW: Check request ID matches current search before updating state
      if (requestId && requestId !== activeSearchId.current) {
        console.log(
          `⚠️ Discarding stale listings response (page ${page}). Request ID: ${requestId}, Current: ${activeSearchId.current}`
        );
        setIsLoading(false);
        setIsLoadingMore(false);
        apiCallsInProgress.current.listings = false;
        return;
      }

      if (response.ok) {
        const result = await response.json();
        console.log("Regular insureds data received:", result);

        let { data, pagination } = result;

        // If we don't have specificRecord yet, try to get it from localStorage
        if (!specificRecord && searchControlNo) {
          const storedRecord = localStorage.getItem("specificControlNoRecord");
          if (storedRecord) {
            specificRecord = JSON.parse(storedRecord);
            console.log(
              "Retrieved specific record from localStorage (second attempt):",
              specificRecord
            );
          }
        }

        // Always filter out any instances of our specific record from the regular results
        // if we have a specific record (regardless of page number)
        if (specificRecord && specificRecord.ControlNo) {
          console.log(
            "Filtering out specific record from results:",
            specificRecord.ControlNo
          );
          const originalLength = data.length;
          data = data.filter(
            (item) =>
              item.ControlNo?.toLowerCase() !==
              specificRecord.ControlNo?.toLowerCase()
          );
          console.log(
            `Filtered out ${
              originalLength - data.length
            } records matching the specific controlNo`
          );
        }

        setTotalInsuredsCount(pagination.total);
        setCurrentPage(pagination?.current_page || 1);
        setHasMoreData(pagination?.current_page < pagination?.last_page);

        // For page 1, add the specific record at the top if it exists
        if (page === 1) {
          console.log("Before adding specific record to top:", {
            hasSpecificRecord: !!specificRecord,
            specificRecordDetails: specificRecord,
            dataLength: data.length,
          });

          const finalData = specificRecord ? [specificRecord, ...data] : data;

          console.log("After adding specific record:", {
            finalDataLength: finalData.length,
            topRecord: finalData[0],
            hasSpecificAtTop: specificRecord
              ? finalData[0].ControlNo === specificRecord.ControlNo
              : false,
          });

          setInsuredsData(finalData);
        } else {
          // For subsequent pages, just add the filtered data
          console.log("Adding data for page " + page + ":", {
            newDataLength: data.length,
          });

          setInsuredsData((prevData) => [...prevData, ...data]);
        }
      } else {
        const errorText = await response.text();
        const errorMsg = `Error loading insured data: ${response.status}`;
        console.error(errorMsg, errorText);
        setError(errorMsg);
        addToast(errorMsg);

        if (page === 1) {
          setInsuredsData([]);
        }
        setHasMoreData(false);
      }
    } catch (error) {
      // ===== FIX 15: Handle Aborted Requests Gracefully =====
      // WHY: Aborted requests shouldn't display as errors
      // HOW: Check for abort error and handle silently
      if (error.name === "AbortError") {
        console.log("🚫 Insureds data fetch was aborted (new search initiated)");
        setIsLoading(false);
        setIsLoadingMore(false);
        apiCallsInProgress.current.listings = false;
        return;
      }
      
      const errorMsg = `Network error: ${error.message}`;
      console.error("Error fetching insureds:", error);
      setError(errorMsg);
      addToast(errorMsg);

      if (page === 1) {
        setInsuredsData([]);
      }
      setHasMoreData(false);
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
      apiCallsInProgress.current.listings = false;
      if (page === 1) {
        abortControllers.current.listings = null;
      }
    }
  };

  const handleSearch = async () => {
    hasTriggeredRef.current = false;
    setHasZoomedToFit(false);
    let controlNo = document.getElementById("ControlNo")?.value || "";

    // Only override controlNo if "search on selection" is true and no record was selected
    // This does NOT affect normal control number search (typed/pasted) when "search on selection" is off
    if (
      searchOnSelection &&
      (!selectedInsured ||
        !selectedInsured.ControlNo ||
        selectedInsured.ControlNo !== controlNo)
    ) {
      controlNo = "";
      if (document.getElementById("ControlNo")) {
        document.getElementById("ControlNo").value = "";
      }
    }

    if (window.currentInfoWindow) {
      window.currentInfoWindow.close();
      window.currentInfoWindow = null;
    }

    const effectiveRadius = (!radius || radius === 0) ? 5 : radius;

    if (!radius || radius === 0) {
      setRadius(5);
    }

    const carrierString = Array.isArray(selectedCarriers)
      ? selectedCarriers.join(",")
      : "";

    // Use the joined array of selected statuses
    const statusString = Array.isArray(selectedStatuses)
      ? selectedStatuses.join(",")
      : "";

    console.log(
      `Searching with control #: ${controlNo}, carrier: ${carrierString}, location: ${location}, radius: ${effectiveRadius}, status: ${statusString}`
    );

    localStorage.setItem("currentFilterCarrier", carrierString);
    localStorage.setItem("currentFilterStatus", statusString);
    localStorage.setItem(
      "currentFilterHasClaimsOnly",
      hasClaimsOnly.toString()
    );

    if (location && controlNo) {
      localStorage.setItem("currentFilterControlNo", controlNo);
    } else {
      localStorage.removeItem("currentFilterControlNo");
    }

    dispatchFilterChangeEvent(carrierString, controlNo);

    setError(null);

    setAddressMarkers([]);
    setInsuredsData([]);
    setCurrentPage(1);
    setHasMoreData(true);
    setZipcodes([]);

    // Abort any ongoing geocoding operations
    if (geocodingAbortController.current) {
      geocodingAbortController.current.abort();
      geocodingAbortController.current = null;
    }

    // Reset all in-progress flags
    Object.keys(apiCallsInProgress.current).forEach((key) => {
      apiCallsInProgress.current[key] = false;
    });

    if (controlNo && !location) {
      // Enhanced control number search flow
      console.log(
        `Enhanced search with control #: ${controlNo} and radius: ${effectiveRadius}`
      );
      if (!searchOnSelection) {
        localStorage.setItem("isControlNoOnlySearch", "true");
      }

      // First try to get the insured's actual address
      const token = localStorage.getItem("accessToken");
      if (token) {
        try {
          setIsLoading(true);

          // Try to fetch the specific insured's data to get the address
          const specificUrl = `${apiUrl}fetch-insureds-listing?status=All&controlNo=${encodeURIComponent(
            controlNo
          )}`;

          const specificResponse = await fetch(specificUrl, {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          });

          if (specificResponse.ok) {
            const result = await specificResponse.json();

            if (result.data && result.data.length > 0) {
              const insuredData = result.data[0];
              const [address] = pickGeocodingAddresses(insuredData.Address1, insuredData.Address2);

              if (address && window.google && window.google.maps) {
                // We have an address, let's geocode it
                const geocoder = new window.google.maps.Geocoder();

                geocoder.geocode({ address }, (results, status) => {
                  if (status === "OK" && results[0]) {
                    const lat = results[0].geometry.location.lat();
                    const lng = results[0].geometry.location.lng();

                    console.log(
                      `Found coordinates for insured: ${lat}, ${lng}`
                    );

                    // Update UI
                    setMapCenter({ lat, lng });
                    setMarkerPosition({ lat, lng });

                    if (mapRef.current) {
                      mapRef.current.panTo({ lat, lng });
                      mapRef.current.setZoom(12);
                    }

                    // Now fetch zipcodes and load data using the actual insured address
                    fetchZipcodesAndLoadData(
                      lat,
                      lng,
                      effectiveRadius,
                      carrierString,
                      controlNo,
                      statusString,
                      hasClaimsOnly
                    );
                  } else {
                    console.log(
                      `Couldn't geocode insured address, falling back to normal flow`
                    );
                    // Fall back to standard approach
                    fetchZipcodesAndLoadData(
                      null,
                      null,
                      effectiveRadius,
                      carrierString,
                      controlNo,
                      statusString,
                      hasClaimsOnly
                    );
                  }
                });
                return;
              }
            }
          }

          // If we got here, we couldn't get or geocode the insured address, use standard flow
          fetchZipcodesAndLoadData(
            null,
            null,
            effectiveRadius,
            carrierString,
            controlNo,
            statusString,
            hasClaimsOnly
          );
        } catch (error) {
          console.error("Error in enhanced control number search:", error);
          // Fall back to original method
          fetchZipcodesAndLoadData(
            null,
            null,
            effectiveRadius,
            carrierString,
            controlNo,
            statusString,
            hasClaimsOnly
          );
        }
      } else {
        // No token, just use original method
        fetchZipcodesAndLoadData(
          null,
          null,
          effectiveRadius,
          carrierString,
          controlNo,
          statusString,
          hasClaimsOnly
        );
      }
    } else if (location) {
      localStorage.setItem("isControlNoOnlySearch", "false");
      // Location search logic
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode(
        { address: location, componentRestrictions: { country: "us" } },
        (results, status) => {
          if (status === "OK" && results[0]) {
            const place = results[0];
            const lat = place.geometry.location.lat();
            const lng = place.geometry.location.lng();

            setMapCenter({ lat, lng });
            setMarkerPosition({ lat, lng });

            if (mapRef.current) {
              mapRef.current.panTo({ lat, lng });
              mapRef.current.setZoom(12);
            }

            // Call orchestration function
            fetchZipcodesAndLoadData(
              lat,
              lng,
              effectiveRadius, // Use effectiveRadius here too
              carrierString,
              controlNo,
              statusString,
              hasClaimsOnly
            );
          } else {
            console.error("Geocoding failed:", status);
            setIsLoading(false);
            setError(
              `Location geocoding failed: ${status}. Please try a different address or zipcode.`
            );
          }
        }
      );
    } else {
      console.warn("No valid location or control number provided.");
      setIsLoading(false);
    }
  };

  const loadMoreData = async () => {
    const carrierString = Array.isArray(selectedCarriers)
      ? selectedCarriers.join(",")
      : "";

    // Allow pagination if carrier is selected even if markerPosition is null
    if (
      (!markerPosition && !carrierString) ||
      isLoadingMore ||
      !hasMoreData ||
      apiCallsInProgress.current.listings
    ) {
      return;
    }

    setIsLoadingMore(true);
    apiCallsInProgress.current.listings = true;
    setError(null);

    const nextPage = currentPage + 1;
    const token = localStorage.getItem("accessToken");

    if (!token) {
      setIsLoadingMore(false);
      apiCallsInProgress.current.listings = false;
      return;
    }

    const statusString = Array.isArray(selectedStatuses)
      ? selectedStatuses.join(",")
      : "";

    try {
      // Build the URL with all filters including status

      let url;

      // Carrier‑only case
      if (!markerPosition) {
        url =
          `${apiUrl}fetch-insureds-listing` +
          `?lat=null` +
          `&long=null` +
          `&zipcodes=` +
          `&page=${nextPage}` +
          (carrierString
            ? `&carrierName=${encodeURIComponent(carrierString)}`
            : ``) +
          (statusString ? `&status=${encodeURIComponent(statusString)}` : ``) +
          (hasClaimsOnly ? `&hasClaimsOnly=true` : ``);
      } else {
        // Location‑based or controlNo‑based case
        url =
          `${apiUrl}fetch-insureds-listing` +
          `?lat=${markerPosition.lat}` +
          `&long=${markerPosition.lng}` +
          `&page=${nextPage}` +
          `&zipcodes=${zipcodes.join(",")}` +
          (carrierString
            ? `&carrierName=${encodeURIComponent(carrierString)}`
            : ``) +
          (statusString ? `&status=${encodeURIComponent(statusString)}` : ``) +
          (hasClaimsOnly ? `&hasClaimsOnly=true` : ``);
      }

      // let url = `${apiUrl}fetch-insureds-listing?lat=${
      //   markerPosition.lat
      // }&long=${
      //   markerPosition.lng
      // }&page=${nextPage}&skip_zipcode_check=true&zipcodes=${zipcodes.join(
      //   ","
      // )}`;

      // // Add carrier filter if present
      // if (carrierString) {
      //   url += `&carrierName=${encodeURIComponent(carrierString)}`;
      // }

      // // Add status filter if present
      // if (statusString) {
      //   url += `&status=${encodeURIComponent(statusString)}`;
      // }

      // if (hasClaimsOnly) {
      //   url += `&hasClaimsOnly=true`;
      // }

      // // If markerPosition is null (carrier-only search), remove lat/lng and zipcodes params
      // if (!markerPosition) {
      //   url = `${apiUrl}fetch-insureds-listing?page=${nextPage}`;
      //   if (carrierString) {
      //     url += `&carrierName=${encodeURIComponent(carrierString)}`;
      //   }
      //   if (statusString) {
      //     url += `&status=${encodeURIComponent(statusString)}`;
      //   }
      //   if (hasClaimsOnly) {
      //     url += `&hasClaimsOnly=true`;
      //   }
      // }

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const newData = await response.json();
        const { data, pagination } = newData;

        if (data && data.length > 0) {
          setInsuredsData((prevData) => [...prevData, ...data]);
          setCurrentPage(pagination.current_page);
          setHasMoreData(pagination.current_page < pagination.last_page);
        } else {
          setHasMoreData(false);
        }
      } else {
        console.error("Failed to fetch more insureds:", response.status);
        setError(`Error loading more data: ${response.status}`);
        setHasMoreData(false);
      }
    } catch (error) {
      console.error("Error fetching more insureds:", error);
      setError(`Network error: ${error.message}`);
      setHasMoreData(false);
    } finally {
      setIsLoadingMore(false);
      apiCallsInProgress.current.listings = false;
    }
  };

  // --- Add cleanup in useEffect ---

  // ===== REMOVED: Redundant Boundary Generation =====
  // WHY: This useEffect was causing boundaries to be redrawn using the first marker's
  // coordinates instead of the actual search center, creating a mismatch where the
  // red marker would be at one location but the radius circle/boundaries at another.
  // HOW: Boundaries are already correctly generated in the main search flow via
  // generateZipcodeBoundary() called from fetchZipcodesAndLoadData(), so this
  // redundant useEffect has been removed.

  useEffect(() => {
    return () => {
      if (window.currentInfoWindow) {
        window.currentInfoWindow.close();
        window.currentInfoWindow = null;
      }

      // Abort any ongoing geocoding operations
      if (geocodingAbortController.current) {
        geocodingAbortController.current.abort();
        geocodingAbortController.current = null;
      }
    };
  }, []);

  const handleRadiusChange = (value, inputType) => {
    const strValue = String(value);

    if (inputType === "text" && strValue === "") {
      setRadius("");
      return;
    }

    const numValue = parseInt(strValue);

    if (numValue === radius) {
      return;
    }

    // Validate the input
    if (!isNaN(numValue)) {
      if (numValue > 150) {
        // Only show toast when directly entered by user
        if (inputType === "text") {
          addToast(
            "Radius cannot exceed the maximum limit of 150 miles",
            "warning"
          );
        }
        setRadius(150);
      } else {
        setRadius(Math.max(0, numValue));
      }
    }
  };

  // Helper function to geocode a single address
  const geocodeAddress = (geocoder, address) => {
    return new Promise((resolve) => {
      geocoder.geocode({ address }, (results, status) => {
        if (status === "OK" && results[0]) {
          resolve({
            lat: results[0].geometry.location.lat(),
            lng: results[0].geometry.location.lng(),
            address: address,
          });
        } else {
          console.warn(
            `Geocoding failed for "${address}" with status: ${status}`
          );
          resolve(null);
        }
      });
    });
  };

  const fetchInsuredPopupDetails = async (controlNo) => {
    if (!controlNo) return null;

    const token = localStorage.getItem("accessToken");
    if (!token) return null;

    try {
      const response = await fetch(
        `${apiUrl}fetch-insured-popup-data/${controlNo}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        const popupData = await response.json();
        console.log("Popup data received:", popupData);

        // Transform the data to match the expected format
        return {
          InsuredName: popupData.InsuredName,
          Address: popupData.Address,
          ControlNo: popupData.ControlNo,
          Carrier: popupData.Carrier,
          Premium: popupData.Premium,
          TIV: popupData.TIV,
          NoOfBuildings: popupData.NoOfBuildings,
          EffectiveDate: popupData.EffectiveDate,
          Status: popupData.Status,
          WFSelectedLimit: popupData.WFSelectedLimit,
        };
      } else {
        console.error("Failed to fetch popup data:", response.status);
        return null;
      }
    } catch (error) {
      console.error("Error fetching popup data:", error);
      return null;
    }
  };

  // Modified handlePlaceSelect function to handle zipcodes better
  const handlePlaceSelect = async () => {
    setIsLoading(true);

    const place = autocompleteRef.current.getPlace();

    setAddressMarkers([]);

    if (!place.geometry) {
      console.log("No details available for this place");

      // Check if current input is a 5-digit zipcode
      const zipCodeRegex = /^\d{5}$/;
      if (zipCodeRegex.test(location.trim())) {
        // If it's a zipcode but Google couldn't find it in autocomplete,
        // manually geocode it
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode(
          {
            address: `${location.trim()} ZIP`,
            componentRestrictions: { country: "us" },
          },
          (results, status) => {
            if (status === "OK" && results[0]) {
              const lat = results[0].geometry.location.lat();
              const lng = results[0].geometry.location.lng();
              const newCenter = { lat, lng };

              console.log("Geocoded zipcode:", lat, lng);
              setLocation(results[0].formatted_address || location);
              setMapCenter(newCenter);
              setMarkerPosition(newCenter);
              setSelectedInsured(null);

              if (mapRef.current) {
                mapRef.current.panTo(newCenter);
                mapRef.current.setZoom(10);
              }

              // Reset pagination-related states
              setCurrentPage(1);
              setHasMoreData(true);
              setZipcodes([]);
              setAddressMarkers([]);

              const controlNo =
                document.getElementById("ControlNo")?.value || "";

              // Call the data fetch function
              fetchZipcodesAndLoadData(
                lat,
                lng,
                radius,
                Array.isArray(selectedCarriers)
                  ? selectedCarriers.join(",")
                  : "",
                controlNo
              );
            }
          }
        );
      }
      return;
    }

    const lat = place.geometry.location.lat();
    const lng = place.geometry.location.lng();
    const newCenter = { lat, lng };

    console.log("Selected place:", lat, lng);
    setLocation(place.formatted_address || place.name);
    setMapCenter(newCenter);
    setMarkerPosition(newCenter);
    setSelectedInsured(null);

    if (mapRef.current) {
      mapRef.current.panTo(newCenter);
      mapRef.current.setZoom(10);
    }

    // Reset pagination-related states when starting a new search
    setCurrentPage(1);
    setHasMoreData(true);
    setZipcodes([]);
    setAddressMarkers([]);

    const controlNo = document.getElementById("ControlNo")?.value || "";

    // Call the data fetch function with the selected location
    fetchZipcodesAndLoadData(
      lat,
      lng,
      radius,
      Array.isArray(selectedCarriers) ? selectedCarriers.join(",") : "",
      controlNo
    );
  };

  const handleControlNoChange = (e) => {
    const controlNo = e.target.value;
    localStorage.setItem("currentFilterControlNo", controlNo);
    dispatchFilterChangeEvent(
      Array.isArray(selectedCarriers) ? selectedCarriers.join(",") : "",
      controlNo
    );
  };

  const handleMarkerDragEnd = (event) => {
    setIsLoading(true);

    const newPosition = {
      lat: event.latLng.lat(),
      lng: event.latLng.lng(),
    };

    setAddressMarkers([]);
    setInsuredsData([]);

    // Update marker position and map center
    setMarkerPosition(newPosition);
    setMapCenter(newPosition);

    // Get location name using reverse geocoding
    if (window.google) {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ location: newPosition }, (results, status) => {
        if (status === "OK" && results[0]) {
          setLocation(results[0].formatted_address);

          // Set the same fixed zoom level
          if (mapRef.current) {
            mapRef.current.setZoom(10);
          }
          // UPDATED: Use the orchestration function
          fetchZipcodesAndLoadData(newPosition.lat, newPosition.lng);
        } else {
          console.error("Geocoder failed due to: " + status);
        }
      });
    }
  };

  const getDynamicRadius = () => {
    if (!markerPosition || addressMarkers.length === 0) return 0;

    let maxDistance = 0;
    addressMarkers.forEach((marker) => {
      if (window.google && window.google.maps && window.google.maps.geometry) {
        const distance =
          window.google.maps.geometry.spherical.computeDistanceBetween(
            new window.google.maps.LatLng(
              markerPosition.lat,
              markerPosition.lng
            ),
            new window.google.maps.LatLng(marker.lat, marker.lng)
          );
        if (distance > maxDistance) {
          maxDistance = distance;
        }
      }
    });

    // Add a small buffer (1km) to ensure all markers are visible
    return maxDistance + 1000;
  };

  const circleRadius = getDynamicRadius();

  const handleScroll = () => {
    if (!insuredListRef.current || isLoading || isLoadingMore || !hasMoreData)
      return;

    const container = insuredListRef.current;
    const scrollPosition = container.scrollTop + container.clientHeight;
    const scrollThreshold = container.scrollHeight - 100; // 100px before bottom

    if (scrollPosition >= scrollThreshold) {
      loadMoreData();
    }
  };

  useEffect(() => {
    const currentListRef = insuredListRef.current;
    if (currentListRef) {
      currentListRef.addEventListener("scroll", handleScroll);
      return () => {
        currentListRef.removeEventListener("scroll", handleScroll);
      };
    }
  }, [hasMoreData, isLoading, isLoadingMore]);

  // Modify the initAutocomplete function to better handle zipcodes
  const initAutocomplete = () => {
    if (
      !inputRef.current ||
      !window.google ||
      !window.google.maps ||
      !window.google.maps.places
    ) {
      console.log("Google Maps API or input reference not available yet");
      return;
    }

    if (inputRef.current && window.google) {
      // Change the type to "geocode" which includes postal codes
      autocompleteRef.current = new window.google.maps.places.Autocomplete(
        inputRef.current,
        {
          fields: [
            "address_components",
            "geometry",
            "name",
            "formatted_address",
          ],
          // This is the key change - use "geocode" instead of "address"
          types: ["geocode"],
          componentRestrictions: { country: "us" },
        }
      );

      // Keep your existing event handlers
      autocompleteRef.current.addListener("place_changed", handlePlaceSelect);
      inputRef.current.addEventListener("input", handleZipCodeInput);
    }
  };

  // Modify handleZipCodeInput to format zipcode inputs properly
  const handleZipCodeInput = (e) => {
    const input = e.target.value;

    setLocation(input);

    const zipCodeRegex = /^\d{5}$/;
    if (zipCodeRegex.test(input.trim())) {
      // For zipcodes, we could add extra behavior here if needed
      console.log("Zipcode detected:", input.trim());

      // We could bias the autocomplete towards postal codes, but this is optional
      if (autocompleteRef.current) {
        const bounds = new window.google.maps.LatLngBounds();
        autocompleteRef.current.setBounds(bounds);
      }
    }
  };

  const getDisplayAddress = (insured) => {
    if (insured.successfulAddress) {
      return insured.successfulAddress;
    }
    return insured.Address2 || insured.Address1 || "N/A"; // Prioritize Address2
  };

  // Format date function for display
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  };

  // Handle map load event
  // const onMapLoad = (map) => {
  //   mapRef.current = map;
  //   setMapLoaded(true);
  // };

  const onMapLoad = (map) => {
    mapRef.current = map;
    setMapLoaded(true);
    console.log("✅ Map onLoad callback fired");
    console.log("📍 Map center:", map.getCenter()?.toString());
    console.log("🔍 Map zoom:", map.getZoom());

    // Ensure map ID is set correctly
    if (window.google && window.google.maps) {
      console.log("✅ Google Maps API available");
      console.log("Current Map ID:", map.mapId);
    }
  };

  // Handle direct input changes
  const handleInputChange = (e) => {
    setLocation(e.target.value);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();

      // Check if autocomplete is available
      if (autocompleteRef.current) {
        // Simulate selecting the first item in the autocomplete dropdown
        const pacContainer = document.querySelector(".pac-container");

        if (
          pacContainer &&
          pacContainer.style.display !== "none" &&
          pacContainer.children.length > 0
        ) {
          // There are visible suggestions, simulate clicking the first one
          const firstItem = pacContainer.querySelector(".pac-item");

          if (firstItem) {
            // Create a synthetic mousedown event to select the first item
            const mousedownEvent = new MouseEvent("mousedown", {
              bubbles: true,
              cancelable: true,
              view: window,
            });

            // Dispatch the event to select the first suggestion
            firstItem.dispatchEvent(mousedownEvent);

            // The place_changed event should be triggered automatically
            // and handlePlaceSelect will be called
            return;
          }
        }

        // If no suggestions or couldn't select, fall back to regular search
        handleSearch();
      } else {
        // If autocomplete isn't initialized, fall back to regular search
        handleSearch();
      }
    }
  };

  const handleListItemClick = (insured) => {
    if (searchOnSelection && insured.ControlNo) {
      // Set the control number in the input field
      const controlNoInput = document.getElementById("ControlNo");
      if (controlNoInput) {
        controlNoInput.value = insured.ControlNo;
      }
      setSelectedControlNo(controlNoInput);
      // Clear location to ensure control number only search
      setLocation("");

      // Clear lat/lng from localStorage
      localStorage.removeItem("currentLatitude");
      localStorage.removeItem("currentLongitude");

      // Get current effective radius
      const effectiveRadius = (!radius || radius === 0) ? 5 : radius;

      // Get current filters
      const carrierString = Array.isArray(selectedCarriers)
        ? selectedCarriers.join(",")
        : "";
      const statusString = Array.isArray(selectedStatuses)
        ? selectedStatuses.join(",")
        : "Bound";

      // Clear existing data
      setAddressMarkers([]);
      setInsuredsData([]);
      setCurrentPage(1);
      setHasMoreData(true);
      setZipcodes([]);

      // Set the flag for control number only search
      localStorage.setItem("isControlNoOnlySearch", "true");

      // Directly call fetchZipcodesAndLoadData for control number search
      fetchZipcodesAndLoadData(
        null, // No lat
        null, // No lng
        effectiveRadius,
        carrierString,
        insured.ControlNo,
        statusString
      );

      return; // Exit early - don't execute the rest of the function
    }

    // Find matching marker for this insured
    const matchingMarker = addressMarkers.find(
      (marker) => marker.insuredData.ControlNo === insured.ControlNo
    );

    if (matchingMarker) {
      // If we found a matching marker, use it to open the InfoWindow
      createInfoWindow(matchingMarker);

      // Pan map to this marker
      if (mapRef.current) {
        mapRef.current.panTo({
          lat: matchingMarker.lat,
          lng: matchingMarker.lng,
        });
        // mapRef.current.setZoom(15); // Zoom in a bit closer
      }
    } else {
      // If no matching marker, geocode the address and create a virtual marker
      if (
        window.google &&
        window.google.maps &&
        (insured.Address1 || insured.Address2)
      ) {
        const [address] = pickGeocodingAddresses(insured.Address1, insured.Address2);
        const geocoder = new window.google.maps.Geocoder();

        geocoder.geocode({ address }, (results, status) => {
          if (status === "OK" && results[0]) {
            const lat = results[0].geometry.location.lat();
            const lng = results[0].geometry.location.lng();

            // Create virtual marker
            const virtualMarker = {
              lat,
              lng,
              insuredData: {
                ...insured,
                successfulAddress: address,
              },
              id: `virtual-marker-${
                insured.ControlNo || Math.random().toString(36).substr(2, 9)
              }`,
            };

            // Open InfoWindow for virtual marker
            createInfoWindow(virtualMarker);

            // Pan map to this location
            if (mapRef.current) {
              mapRef.current.panTo({ lat, lng });
              // mapRef.current.setZoom(15);
            }
          } else {
            console.warn("Could not geocode address for list item:", address);

            // Even if geocoding fails, still show selected insured in mobile panel
            setSelectedInsured(insured);
            if (isMobileView) {
              setBottomPanelOpen(true);
            }
          }
        });
      } else {
        // No address to geocode, just set as selected
        setSelectedInsured(insured);
        if (isMobileView) {
          setBottomPanelOpen(true);
        }
      }
    }
  };

  const debouncedFitBounds = useRef(
    debounce((markers, markerPos) => {
      if (!mapRef.current || markers.length === 0) return;

      const bounds = new window.google.maps.LatLngBounds();
      if (markerPos) {
        bounds.extend(
          new window.google.maps.LatLng(markerPos.lat, markerPos.lng)
        );
      }

      markers.forEach((marker) => {
        bounds.extend(new window.google.maps.LatLng(marker.lat, marker.lng));
      });

      mapRef.current.fitBounds(bounds);
      console.log("Map bounds adjusted (debounced)");
    }, 300) // 300ms debounce
  ).current;

  const fitBoundsToAllMarkers = () => {
    debouncedFitBounds(addressMarkers, markerPosition);
  };

  const getDisplayedRadius = () => {
    if (!radius || radius === 0) {
      return 5;
    }
    return radius;
  };

  useEffect(() => {
    if (mapLoaded && window.google) {
      initAutocomplete();
    }
  }, [mapLoaded]);

  // After geocoding completes for a control number search, re-center the
  // halo circle on the actual insured marker if the initial geocode drifted.
  // SAFE: only updates zipcodeBoundaries (the visual circle), NOT markerPosition
  // (which would cascade into the consolidated search effect).
  useEffect(() => {
    if (isGeocodingInProgress || addressMarkers.length === 0) return;
    if (!specificControlNoRecord) return; // Only for control number searches

    const searchedControlNo = String(specificControlNoRecord.ControlNo);
    const matchingMarker = addressMarkers.find(
      (m) => String(m.insuredData?.ControlNo) === searchedControlNo
    );

    if (!matchingMarker || !markerPosition) return;

    const dist = Math.abs(matchingMarker.lat - markerPosition.lat) +
                 Math.abs(matchingMarker.lng - markerPosition.lng);

    // Only re-center if the marker is meaningfully offset (>~100m)
    if (dist <= 0.001) return;

    console.log("🔄 Re-centering halo on insured marker (drift:", dist.toFixed(5), "deg)");

    const center = { lat: matchingMarker.lat, lng: matchingMarker.lng };
    const effectiveRadius = radius || 5;
    const radiusInMeters = effectiveRadius * 1609.34;
    const numPoints = 32;
    const points = [];
    for (let i = 0; i < numPoints; i++) {
      const angle = (i / numPoints) * 360 * (Math.PI / 180);
      points.push({
        lat: center.lat + (radiusInMeters / 111111) * Math.cos(angle),
        lng: center.lng + (radiusInMeters / (111111 * Math.cos(center.lat * (Math.PI / 180)))) * Math.sin(angle),
      });
    }
    points.push(points[0]);
    setZipcodeBoundaries([{ paths: points, isSearchArea: true }]);

    // Pan to the correct location without setting markerPosition
    if (mapRef.current) {
      mapRef.current.panTo(center);
    }
  }, [isGeocodingInProgress, addressMarkers, specificControlNoRecord]);

  useEffect(() => {
    if (location === "") {
      setAddressMarkers([]);
    }
  }, [location]);

  // useEffect(() => {
  //   if (!isGeocodingInProgress && addressMarkers.length > 0) {
  //     fitBoundsToAllMarkers();
  //   }
  // }, [addressMarkers, isGeocodingInProgress]);

  useEffect(() => {
    if (
      !isGeocodingInProgress &&
      addressMarkers.length > 0 &&
      !hasZoomedToFit
    ) {
      fitBoundsToAllMarkers();
      setHasZoomedToFit(true);
    }
  }, [addressMarkers, isGeocodingInProgress, hasZoomedToFit]);

  useEffect(() => {
    if (!selectedInsured && window.currentInfoWindow) {
      window.currentInfoWindow.close();
      window.currentInfoWindow = null;
    }
  }, [selectedInsured]);

  const createInfoWindow = (marker) => {
    if (!window.google || !mapRef.current || !marker.insuredData) return;

    // Close any existing InfoWindow
    if (window.currentInfoWindow) {
      window.currentInfoWindow.close();
      window.currentInfoWindow = null;
    }

    // if (searchOnSelection && marker.insuredData.ControlNo) {
    //   // Set the control number in the input field
    //   const controlNoInput = document.getElementById("ControlNo");
    //   if (controlNoInput) {
    //     controlNoInput.value = marker.insuredData.ControlNo;
    //   }

    //   // Clear location to ensure control number only search
    //   setLocation("");

    //   // Clear lat/lng from localStorage
    //   localStorage.removeItem("currentLatitude");
    //   localStorage.removeItem("currentLongitude");

    //   // Get current effective radius
    //   const effectiveRadius = radius === 0 ? 5 : radius;

    //   // Get current filters
    //   const carrierString = Array.isArray(selectedCarriers)
    //     ? selectedCarriers.join(",")
    //     : "";
    //   const statusString = Array.isArray(selectedStatuses)
    //     ? selectedStatuses.join(",")
    //     : "Bound";

    //   // Clear existing data
    //   setAddressMarkers([]);
    //   setInsuredsData([]);
    //   setCurrentPage(1);
    //   setHasMoreData(true);
    //   setZipcodes([]);

    //   // Set the flag for control number only search
    //   localStorage.setItem("isControlNoOnlySearch", "true");

    //   // Directly call fetchZipcodesAndLoadData for control number search
    //   fetchZipcodesAndLoadData(
    //     null, // No lat
    //     null, // No lng
    //     effectiveRadius,
    //     carrierString,
    //     marker.insuredData.ControlNo,
    //     statusString
    //   );

    //   return; // Exit early - don't show InfoWindow, search will happen
    // }

    const insuredData = marker.insuredData;

    // Show loading state first
    const loadingContent = `
    <div style="padding: 12px; text-align: center;">
      <div class="spinner-border spinner-border-sm text-primary" role="status"></div>
      <p style="margin-top: 10px; font-size: 14px;">Loading details...</p>
    </div>
  `;

    // Create invisible marker for the InfoWindow
    const infoMarker = new window.google.maps.Marker({
      position: { lat: marker.lat, lng: marker.lng },
      map: mapRef.current,
      visible: false,
    });

    // Create InfoWindow with loading state
    const infoWindow = new window.google.maps.InfoWindow({
      content: loadingContent,
      maxWidth: 320,
      pixelOffset: new window.google.maps.Size(0, -40), // Move the InfoWindow up by 40 pixels
    });

    // Add close listener to reset states
    infoWindow.addListener("closeclick", () => {
      setActiveMarkerId(null);
      setSelectedInsured(null);
      window.currentInfoWindow = null;
    });

    // Store reference and open InfoWindow
    window.currentInfoWindow = infoWindow;
    infoWindow.open(mapRef.current, infoMarker);

    // Set active marker and selected insured
    setActiveMarkerId(marker.id);
    setSelectedInsured({
      ...insuredData,
      isLoading: true,
    });

    if (isMobileView) {
      setBottomPanelOpen(true);
    }

    // Always fetch fresh data for the InfoWindow
    const controlNo = insuredData.ControlNo;
    if (controlNo) {
      // Fetch details with a small delay to ensure DOM is ready
      setTimeout(() => {
        fetchInsuredPopupDetails(controlNo)
          .then((popupData) => {
            if (!popupData || !window.currentInfoWindow) return;

            console.log(popupData);
            // Create complete content with fetched data
            const updatedContent = `
            <div style="padding: 12px; max-width: 300px;">
              <h6 style="font-weight: bold; margin-bottom: 10px; font-size: 16px;">
                ${
                  popupData.InsuredName ||
                  insuredData.InsuredPolicyName ||
                  "Insured Account"
                }
              </h6>
              <div style="margin-bottom: 8px;">
                <p style="font-size: 14px; margin: 4px 0;">
                  <strong>Address:</strong> ${
                    popupData.Address ||
                    insuredData.Address2 ||
                    insuredData.Address1 ||
                    "N/A"
                  }
                </p>
                <p style="font-size: 14px; margin: 4px 0;">
                  <strong>Control No:</strong> ${
                    popupData.ControlNo || insuredData.ControlNo || "N/A"
                  }
                </p>
                <p style="font-size: 14px; margin: 4px 0;">
                  <strong>Carrier:</strong> ${popupData.Carrier || "N/A"}
                </p>
                <p style="font-size: 14px; margin: 4px 0;">
                  <strong>Policy Type:</strong> ${popupData.PolicyType || "N/A"}
                </p>
                <p style="font-size: 14px; margin: 4px 0;">
                  <strong>Premium:</strong> ${formatMoney(popupData.Premium)}
                </p>
                <p style="font-size: 14px; margin: 4px 0;">
                  <strong>TIV:</strong> ${formatMoney(popupData.TIV)}
                </p>
                <p style="font-size: 14px; margin: 4px 0;">
                  <strong>No. of Buildings:</strong> ${
                    popupData.NoOfBuildings || "N/A"
                  }
                </p>
                <p style="font-size: 14px; margin: 4px 0;">
                  <strong>Effective Date:</strong> ${
                    formatDate(popupData.EffectiveDate) || "N/A"
                  }
                </p>
                 <p style="font-size: 14px; margin: 4px 0;">
                  <strong>Status:</strong> ${popupData.Status || "N/A"}
                </p>
                <p style="font-size: 14px; margin: 4px 0;">
                  <strong>WildFire Limit:</strong> 
                  ${popupData.WFSelectedLimit || "N/A"}
                </p>
       ${
         searchOnSelection
           ? `
  <button id="searchSelectionBtn" style="
  background-color: #4CAF50;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
">
  <i class="fa fa-search"></i> Search on Selection
</button>
`
           : ""
       }

              </div>
            </div>
          `;

            // Update InfoWindow content
            window.currentInfoWindow.setContent(updatedContent);

            // Add listener for the search button inside InfoWindow
            setTimeout(() => {
              const searchBtn = document.getElementById("searchSelectionBtn");
              if (searchBtn) {
                searchBtn.addEventListener("click", () => {
                  const controlNoInput = document.getElementById("ControlNo");
                  if (controlNoInput) {
                    controlNoInput.value = popupData.ControlNo;
                  }

                  setLocation("");
                  localStorage.removeItem("currentLatitude");
                  localStorage.removeItem("currentLongitude");

                  const effectiveRadius = (!radius || radius === 0) ? 5 : radius;
                  const carrierString = Array.isArray(selectedCarriers)
                    ? selectedCarriers.join(",")
                    : "";
                  const statusString = Array.isArray(selectedStatuses)
                    ? selectedStatuses.join(",")
                    : "Bound";

                  setAddressMarkers([]);
                  setInsuredsData([]);
                  setCurrentPage(1);
                  setHasMoreData(true);
                  setZipcodes([]);

                  localStorage.setItem("isControlNoOnlySearch", "true");

                  fetchZipcodesAndLoadData(
                    null,
                    null,
                    effectiveRadius,
                    carrierString,
                    popupData.ControlNo,
                    statusString
                  );

                  // Optionally close the InfoWindow after search
                  if (window.currentInfoWindow) {
                    window.currentInfoWindow.close();
                    window.currentInfoWindow = null;
                  }
                });
              }
            }, 150);

            // Update selected insured for mobile view
            setSelectedInsured({
              ...insuredData,
              ...popupData,
              isLoading: false,
            });
          })
          .catch((error) => {
            console.error("Error fetching insured details:", error);

            if (!window.currentInfoWindow) return;

            // Show error in InfoWindow with basic data
            const errorContent = `
            <div style="padding: 12px; max-width: 300px;">
              <h6 style="font-weight: bold; margin-bottom: 10px; font-size: 16px;">
                ${insuredData.InsuredPolicyName || "Insured Account"}
              </h6>
              <p style="font-size: 14px; margin: 4px 0;">
                <strong>Address:</strong> ${
                  insuredData.Address2 || insuredData.Address1 || "N/A"
                }
              </p>
              <p style="font-size: 14px; margin: 4px 0;">
                <strong>Control No:</strong> ${insuredData.ControlNo || "N/A"}
              </p>
               <p style="font-size: 14px; margin: 4px 0;">
      <strong>Status:</strong> ${insuredData.Status || "N/A"}
    </p>
              <p style="color: #dc3545; font-size: 13px; margin-top: 10px;">
                Error loading additional details. Please try again.
              </p>
            </div>
          `;

            window.currentInfoWindow.setContent(errorContent);

            setSelectedInsured({
              ...insuredData,
              isLoading: false,
            });
          });
      }, 100); // Small delay to ensure stable operation
    } else {
      // No control number, just show what we have
      const basicContent = `
      <div style="padding: 12px; max-width: 300px;">
        <h6 style="font-weight: bold; margin-bottom: 10px; font-size: 16px;">
          ${insuredData.InsuredPolicyName || "Insured Location"}
        </h6>
        <p style="font-size: 14px; margin: 4px 0;">
          <strong>Address:</strong> ${
            insuredData.Address2 || insuredData.Address1 || "N/A"
          }
        </p>
        <p style="font-size: 14px; margin: 4px 0;">
          <strong>Control No:</strong> Not Available
        </p>
        <p style="font-size: 14px; margin: 4px 0;">
      <strong>Status:</strong> ${insuredData.Status || "N/A"}
    </p>
        <p style="color: #6c757d; font-size: 13px; margin-top: 10px;">
          Limited information available for this location
        </p>
      </div>
    `;

      window.currentInfoWindow.setContent(basicContent);
      setSelectedInsured(insuredData);
    }
  };

  // Filter markers and list rows whose geocoded position falls outside the selected radius. The backend returns records by zipcode, but
  // zipcode polygons extend past the radius circle, leaving edge-of-zip records visible. Once we have lat/lng from geocoding we can trim precisely.
  const radiusFilteredMarkers = useMemo(() => {
    if (!markerPosition || !radius) return addressMarkers;
    return addressMarkers.filter((m) => {
      if (typeof m?.lat !== "number" || typeof m?.lng !== "number") return true;
      return (
        haversineMiles(markerPosition.lat, markerPosition.lng, m.lat, m.lng) <=
        radius
      );
    });
  }, [addressMarkers, markerPosition, radius]);

  const insuredsWithinRadius = useMemo(() => {
    if (!markerPosition || !radius || addressMarkers.length === 0) {
      return insuredsData;
    }
    // Build lookup of ControlNo → inside-radius boolean from filtered markers.
    const insideIds = new Set(
      radiusFilteredMarkers
        .map((m) => m?.insuredData?.ControlNo)
        .filter(Boolean)
        .map(String)
    );
    const knownIds = new Set(
      addressMarkers
        .map((m) => m?.insuredData?.ControlNo)
        .filter(Boolean)
        .map(String)
    );
    // The record the user typed a control # for is the radius seed; never hide it.
    const pinnedId = specificControlNoRecord?.ControlNo
      ? String(specificControlNoRecord.ControlNo)
      : null;
    return insuredsData.filter((row) => {
      const id = row?.ControlNo ? String(row.ControlNo) : null;
      if (id && pinnedId && id === pinnedId) return true;
      // If this record hasn't been geocoded yet, keep it (geocoding in progress).
      if (!id || !knownIds.has(id)) return true;
      return insideIds.has(id);
    });
  }, [
    insuredsData,
    addressMarkers,
    radiusFilteredMarkers,
    markerPosition,
    radius,
    specificControlNoRecord,
  ]);

  // (carrier breakdown precision): mirror the list's per-address
  // Haversine filter into the carrier-breakdown aggregation. The sidebar fetches
  // `/fetch-carriers-summary` server-side and can't redo the Haversine on its
  // own (ConsolidatedInsuredData has no lat/lng), so we hand it the list of
  // ControlNos that are geocoded and confirmed *outside* the circle. The
  // backend applies `cid.ControlNo NOT IN (:excludeControlNos)` so sidebar
  // totals match what the user sees on the map and in the list.
  const outOfRadiusControlNos = useMemo(() => {
    if (!markerPosition || !radius || addressMarkers.length === 0) return [];
    const pinnedId = specificControlNoRecord?.ControlNo
      ? String(specificControlNoRecord.ControlNo)
      : null;
    return addressMarkers
      .filter((m) => {
        if (typeof m?.lat !== "number" || typeof m?.lng !== "number") return false;
        const id = m?.insuredData?.ControlNo
          ? String(m.insuredData.ControlNo)
          : null;
        if (id && pinnedId && id === pinnedId) return false; // never exclude the seed
        return (
          haversineMiles(markerPosition.lat, markerPosition.lng, m.lat, m.lng) >
          radius
        );
      })
      .map((m) => m?.insuredData?.ControlNo)
      .filter(Boolean)
      .map(String);
  }, [addressMarkers, markerPosition, radius, specificControlNoRecord]);

  useEffect(() => {
    const value = outOfRadiusControlNos.join(",");
    if (value) {
      localStorage.setItem("currentExcludeControlNos", value);
    } else {
      localStorage.removeItem("currentExcludeControlNos");
    }
    // Nudge the sidebar to re-aggregate with the fresh exclusion list.
    window.dispatchEvent(
      new CustomEvent("exclude-controlnos-updated", {
        detail: { excludeControlNos: value },
      })
    );
  }, [outOfRadiusControlNos]);

  return (
    <>
      <div className="dashboard-title mb-2 mx-2">
        <h1>Geographical Concentration</h1>
        <div className="dropdown-container">
          <Dropdown>
            <Dropdown.Toggle
              variant="light"
              id="dropdown-basic"
              className="rounded-circle border-0 bg-transparent"
            >
              <div className="menu-icon">
                <BsGrid3X3GapFill />
              </div>
            </Dropdown.Toggle>

            <Dropdown.Menu className="dropdown-menu-custom">
              <Dropdown.Item href="/dashboard" className="dropdown-item-custom">
                <BsFillEnvelopeFill className="dropdown-icon" /> Quote/Bind
                Email
              </Dropdown.Item>
              <Dropdown.Item
                href="/geographical-concentration"
                className="dropdown-item-custom"
              >
                <IoMap className="dropdown-icon" /> Geographical Concentration
              </Dropdown.Item>
              <Dropdown.Item
                href="/renewals-dashboard"
                className="dropdown-item-custom"
              >
                <FiRefreshCw className="dropdown-icon" /> Renewals
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>

      <div className="mobile-filter mb-3">
        <button className="mobile-btn" onClick={() => setIsOpen(true)}>
          <IoFilterSharp /> Open Filters
        </button>
      </div>

      <div className={`side-panel ${isOpen ? "open" : ""}`}>
        <button className="close-btn" onClick={() => setIsOpen(false)}>
          <IoCloseCircle />
        </button>
        <div
          className="container-fluid filter-area d-flex align-items-center gap-3 rounded-0"
          style={{
            background: "#0a2a7b",
            padding: "16px",
            borderRadius: "8px",
          }}
        >
          <div className="flex-grow-1">
            <input
              type="text"
              name="ControlNo"
              id="ControlNo"
              className={`form-control ${
                document.getElementById("ControlNo")?.value
                  ? "control-no-highlight"
                  : ""
              }`}
              placeholder="Enter Control No."
              onChange={handleControlNoChange}
              onKeyDown={handleKeyDown}
              onClick={(e) => e.target.select()}
              style={{
                backgroundColor: document.getElementById("ControlNo")?.value
                  ? "#F5E6A3"
                  : "",
                border: document.getElementById("ControlNo")?.value
                  ? "2px solid #E6D690"
                  : "",
              }}
            />
          </div>

          <div className="location-autocomplete-container flex-grow-1">
            <input
              type="text"
              name="Location"
              id="Location"
              className="form-control"
              placeholder="Enter Zipcode or City"
              ref={inputRef}
              value={location}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onClick={(e) => e.target.select()}
            />
          </div>
          <div className="d-flex align-items-center">
            <label className="text-white fw-bold me-2">Radius:</label>
            <input
              type="range"
              className="form-range custom-slider"
              min="0"
              max="100"
              value={getDisplayedRadius() <= 100 ? getDisplayedRadius() : 100}
              onChange={(e) => handleRadiusChange(e.target.value, "slider")}
              onKeyDown={handleKeyDown}
              style={{ width: "120px" }}
            />
            <div
              className="bg-white text-dark px-2 py-1 rounded ms-2"
              style={{ minWidth: "60px" }}
            >
              <input
                type="number"
                className="form-control form-control-sm border-0"
                min="0"
                max="150"
                value={getDisplayedRadius()}
                onChange={(e) => handleRadiusChange(e.target.value, "text")}
                onClick={(e) => e.target.select()}
                onKeyDown={handleKeyDown}
                style={{ width: "60px", textAlign: "center" }}
              />
            </div>
          </div>
          <div
            className="flex-grow-1"
            style={{ minWidth: "180px", position: "relative" }}
          >
            <Dropdown
              show={isDropdownOpen}
              onToggle={(isOpen) => setIsDropdownOpen(isOpen)}
              autoClose="outside"
            >
              <Dropdown.Toggle
                variant="light"
                id="dropdown-carriers"
                className={`
    w-100 text-start d-flex justify-content-between align-items-center
    ${selectedCarriers.length > 0 ? "carrier-highlight" : ""}
  `}
                style={{
                  height: "38px",
                  // Remove backgroundColor and border here; let the class handle it
                }}
              >
                <span className="text-truncate">
                  {selectedCarriers.length === 0
                    ? "All Carriers"
                    : selectedCarriers.length === 1
                    ? selectedCarriers[0]
                    : `${selectedCarriers.length} carriers selected`}
                </span>
              </Dropdown.Toggle>

              <Dropdown.Menu className="w-100 carrier-dropdown-menu">
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                  }}
                >
                  <div className="px-2 py-1" style={{ width: "100%" }}>
                    <div
                      className="form-check"
                      style={{ display: "flex", alignItems: "center" }}
                    >
                      <input
                        className="form-check-input me-2"
                        type="checkbox"
                        id="allCarriers"
                        checked={selectedCarriers.length === 0}
                        onChange={() => {
                          setSelectedCarriers([]);
                          localStorage.setItem("currentFilterCarrier", "");
                          dispatchFilterChangeEvent(
                            "",
                            document.getElementById("ControlNo")?.value || ""
                          );
                          setIsDropdownOpen(false);
                        }}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <label
                        className="form-check-label"
                        htmlFor="allCarriers"
                        onClick={(e) => e.stopPropagation()}
                      >
                        All Carriers
                      </label>
                    </div>
                  </div>
                  <Dropdown.Divider />
                  {availableCarriers.map((carrier, index) => (
                    <div
                      className="px-2 py-1"
                      key={`carrier-check-${index}`}
                      onClick={(e) => e.stopPropagation()}
                      style={{ width: "100%" }}
                    >
                      <div
                        className="form-check"
                        style={{ display: "flex", alignItems: "center" }}
                      >
                        <input
                          className="form-check-input me-2"
                          type="checkbox"
                          id={`carrier-${index}`}
                          value={carrier}
                          checked={selectedCarriers.includes(carrier)}
                          onChange={(e) => {
                            const isChecked = e.target.checked;
                            let newSelection;

                            if (isChecked) {
                              newSelection = [...selectedCarriers, carrier];
                            } else {
                              newSelection = selectedCarriers.filter(
                                (c) => c !== carrier
                              );
                            }

                            setSelectedCarriers(newSelection);
                            const carrierString = newSelection.join(",");
                            localStorage.setItem(
                              "currentFilterCarrier",
                              carrierString
                            );
                            dispatchFilterChangeEvent(
                              carrierString,
                              document.getElementById("ControlNo")?.value || ""
                            );
                          }}
                        />
                        <label
                          className="form-check-label"
                          htmlFor={`carrier-${index}`}
                        >
                          {carrier}
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              </Dropdown.Menu>
            </Dropdown>
          </div>
          {/* Status Dropdown */}
          <div
            className="flex-grow-1"
            style={{ minWidth: "150px", position: "relative" }}
          >
            <Dropdown
              show={isStatusDropdownOpen}
              onToggle={(isOpen) => setIsStatusDropdownOpen(isOpen)}
              autoClose="outside"
            >
              <Dropdown.Toggle
                variant="light"
                id="dropdown-statuses"
                className={`
    w-100 text-start d-flex justify-content-between align-items-center
    ${
      selectedStatuses.length !== 0 &&
      selectedStatuses.length !== availableStatuses.length
        ? "status-highlight"
        : ""
    }
  `}
                style={{
                  height: "38px",
                  // Let the CSS class handle background/border when highlighted
                }}
              >
                <span className="text-truncate">
                  {selectedStatuses.length === 0
                    ? "All Statuses"
                    : selectedStatuses.length === availableStatuses.length
                    ? "All Statuses"
                    : selectedStatuses.length === 1
                    ? selectedStatuses[0]
                    : `${selectedStatuses.length} statuses selected`}
                </span>
              </Dropdown.Toggle>

              <Dropdown.Menu className="w-100 status-dropdown-menu">
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                  }}
                >
                  <div className="px-2 py-1" style={{ width: "100%" }}>
                    <div
                      className="form-check"
                      style={{ display: "flex", alignItems: "center" }}
                    >
                      <input
                        className="form-check-input me-2"
                        type="checkbox"
                        id="allStatuses"
                        checked={
                          selectedStatuses.length === availableStatuses.length
                        }
                        onChange={() => {
                          // If all are selected, clear selection (will default to "Bound")
                          // If not all are selected, select all
                          const newSelection =
                            selectedStatuses.length === availableStatuses.length
                              ? []
                              : [...availableStatuses];

                          setSelectedStatuses(newSelection);
                          handleStatusChange(newSelection);
                          if (
                            newSelection.length === availableStatuses.length
                          ) {
                            setIsStatusDropdownOpen(false);
                          }
                        }}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <label
                        className="form-check-label"
                        htmlFor="allStatuses"
                        onClick={(e) => e.stopPropagation()}
                      >
                        All Statuses
                      </label>
                    </div>
                  </div>
                  <Dropdown.Divider />
                  {availableStatuses.map((status, index) => (
                    <div
                      className="px-2 py-1"
                      key={`status-check-${index}`}
                      onClick={(e) => e.stopPropagation()}
                      style={{ width: "100%" }}
                    >
                      <div
                        className="form-check"
                        style={{ display: "flex", alignItems: "center" }}
                      >
                        <input
                          className="form-check-input me-2"
                          type="checkbox"
                          id={`status-${index}`}
                          value={status}
                          checked={selectedStatuses.includes(status)}
                          onChange={(e) => {
                            const isChecked = e.target.checked;
                            let newSelection;

                            if (isChecked) {
                              newSelection = [...selectedStatuses, status];
                            } else {
                              newSelection = selectedStatuses.filter(
                                (s) => s !== status
                              );
                            }

                            setSelectedStatuses(newSelection);
                            handleStatusChange(newSelection);
                          }}
                        />
                        <label
                          className="form-check-label"
                          htmlFor={`status-${index}`}
                        >
                          {status}
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              </Dropdown.Menu>
            </Dropdown>
          </div>
          {/* <div className="flex-grow-1" style={{ minWidth: "120px" }}>
            <select
              className="form-select"
              value={selectedStatus}
              onChange={handleStatusChange}
              style={{
                height: "38px", // Fixed height
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              <option value="Quoted">Quoted</option>
              <option value="Bound">Bound</option>
              <option value="Bind Order Received">Bind Order Received</option>
            </select>
          </div> */}
          <div>
            <button
              type="button"
              className="btn btn-success"
              onClick={handleSearch}
              style={{ backgroundColor: "#4CAF50", borderColor: "#4CAF50" }}
            >
              Search
            </button>
          </div>
          <div>
            <button
              type="button"
              className="btn btn-light"
              onClick={handleClear}
            >
              Clear
            </button>
          </div>
        </div>
      </div>
      <div className="map-grid">
        <div className="map-info">
          <h5 className="gc-heading mx-2 mt-2">
            <strong>Insured found:</strong> {insuredsWithinRadius.length}
            {insuredsWithinRadius.length !== totalInsuredsCount &&
            totalInsuredsCount > 0 ? (
              <small className="text-muted ms-2">
                (of {totalInsuredsCount} in nearby ZIPs)
              </small>
            ) : null}
          </h5>
          <h5 className="gc-heading mx-2">
            Search results for '{location || " "}'
          </h5>

          <div className="gc-toggle mb-2">
            {/* Search on Selection Toggle */}
            <div className="filter-control-group mb-0">
              <label className="toggle-switch toggle-sm">
                <input
                  type="checkbox"
                  checked={searchOnSelection}
                  onChange={(e) => setSearchOnSelection(e.target.checked)}
                  id="searchOnSelectionToggle"
                />
                <span className="toggle-slider"></span>
              </label>
              <label className="toggleLabel" htmlFor="searchOnSelectionToggle">
                Search on Selection
              </label>
            </div>
            {/* Has Claims Only Toggle */}
            <div className="filter-control-group mb-0">
              <label className="toggle-switch toggle-sm">
                <input
                  type="checkbox"
                  checked={hasClaimsOnly}
                  onChange={handleHasClaimsOnlyChange}
                  id="hasClaimsToggle"
                />
                <span className="toggle-slider"></span>
              </label>
              <label className="toggleLabel" htmlFor="hasClaimsToggle">
                Has Claims
              </label>
            </div>
          </div>

          <div
            className={`sr-box ${hover ? "hovered" : ""}`}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            ref={insuredListRef}
          >
            {isLoading ? (
              <div className="text-center py-4">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2">Loading results...</p>
              </div>
            ) : (
              <ul className="gc-grid">
                {insuredsWithinRadius.length > 0 ? (
                  insuredsWithinRadius.map((insured, index) => (
                    <li
                      ref={setListItemRef(insured.ControlNo)}
                      key={insured.QuoteGUID || index}
                      className={[
                        selectedInsured?.QuoteGUID === insured.QuoteGUID
                          ? "active"
                          : "",
                        hoveredControlNo &&
                        insured.ControlNo &&
                        insured.ControlNo.toString().toLowerCase() ===
                          hoveredControlNo.toString().toLowerCase()
                          ? "hover-match"
                          : "",
                      ].join(" ")}
                      onMouseOver={() => {
                        const cn = insured?.ControlNo;
                        if (cn) {
                          setHoveredRecord(cn);
                        }
                      }}
                      onMouseOut={() => setHoveredRecord(null)}
                      onClick={() => handleListItemClick(insured)}
                    >
                      <h3>
                        {insured.InsuredPolicyName || "N/A"}
                        <span>{getDisplayAddress(insured)}</span>
                      </h3>
                      <div className="list-card">
                        <div>Control No.</div>
                        <div>
                          {insured.ControlNo &&
                          selectedControlNo &&
                          insured.ControlNo.toString().toLowerCase() ===
                            selectedControlNo.toString().toLowerCase() ? (
                            <span
                              style={{
                                color: "#1976d2",
                                textDecoration: "underline",
                                cursor: "default",
                                fontWeight: "bold",
                                background: "rgba(25, 118, 210, 0.10)",
                                borderRadius: "4px",
                                padding: "0 4px",
                              }}
                              tabIndex={-1}
                              aria-disabled="true"
                            >
                              {insured.ControlNo}
                            </span>
                          ) : (
                            insured.ControlNo || "N/A"
                          )}
                        </div>
                        <div>Effective date:</div>
                        <div>{formatDate(insured.EffectiveDate)}</div>
                        <div>Carrier:</div>
                        <div>{insured.Carrier || "N/A"}</div>
                        <div>Policy Type:</div>
                        <div>{insured.PolicyType || "N/A"}</div>
                        <div>Premium:</div>
                        <div>{formatMoney(insured.Premium)}</div>
                        <div>TIV:</div>
                        <div>{formatMoney(insured.PropertyTIV)}</div>
                        <div>No. of Buildings:</div>
                        <div>{insured.NoOfBuildings || "N/A"}</div>
                        <div>Status:</div>
                        <div>{insured.Status || "N/A"}</div>
                        <div>WildFire limit:</div>
                        <div>{insured.WFSelectedLimit || "N/A"}</div>
                        {insured?.HasClaim && (
                          <>
                            <div>Claim ID:</div>
                            <div>{insured.ClaimID || "N/A"}</div>

                            <div>Date Reported:</div>
                            <div>
                              {insured.ClaimDateReported
                                ? formatDate(insured.ClaimDateReported)
                                : "N/A"}
                            </div>

                            <div>Loss Date:</div>
                            <div>
                              {insured.ClaimLossDate
                                ? formatDate(insured.ClaimLossDate)
                                : "N/A"}
                            </div>

                            <div>Loss Type:</div>
                            <div>{insured.ClaimLossType || "N/A"}</div>

                            <div>Claim Status:</div>
                            <div>{insured.ClaimStatus || "N/A"}</div>

                            <div>Total Incurred:</div>
                            <div>{insured.ClaimTotalIncurred || "N/A"}</div>

                            <div>Expense Paid:</div>
                            <div>{insured.ClaimExpensePaid || "N/A"}</div>
                          </>
                        )}
                      </div>
                      {searchOnSelection && (
                        <button>
                          <FaSearch /> Search on Selection
                        </button>
                      )}
                    </li>
                  ))
                ) : (
                  <li>
                    <p>
                      No insureds found in this area. Try adjusting your search.
                    </p>
                  </li>
                )}
                {/* Added loading indicator for infinite scroll */}
                {isLoadingMore && (
                  <li className="text-center py-3">
                    <div
                      className="spinner-border spinner-border-sm text-primary me-2"
                      role="status"
                    >
                      <span className="visually-hidden">Loading more...</span>
                    </div>
                    <span>Loading more results...</span>
                  </li>
                )}

                {/* Added end of list message */}
                {!isLoading &&
                  !isLoadingMore &&
                  !hasMoreData &&
                  insuredsWithinRadius.length > 0 && (
                    <li className="text-center py-2 text-muted">
                      <small>End of results</small>
                    </li>
                  )}
              </ul>
            )}
          </div>
        </div>
        <div className="map-area">
          {isGeocodingInProgress && (
            <div className="geocoding-status position-absolute top-0 end-0 bg-white m-2 p-2 rounded shadow">
              <div className="d-flex align-items-center">
                <div
                  className="spinner-border spinner-border-sm text-primary me-2"
                  role="status"
                >
                  <span className="visually-hidden">Loading...</span>
                </div>
                <span>Geocoding addresses...</span>
              </div>
            </div>
          )}
          {console.log("🗺️ About to render LoadScript with apiKey:", mapApiKey)}
          <LoadScript
            googleMapsApiKey={mapApiKey}
            libraries={googleMapsLibraries}
            onLoad={() => {
              setMapLoaded(true);
              console.log("✅ Google Maps script loaded successfully");
            }}
            onError={(error) => {
              console.error("❌ Error loading Google Maps script:", error);
            }}
          >
            <GoogleMap
              mapContainerStyle={containerStyle}
              center={mapCenter}
              zoom={10}
              onLoad={onMapLoad}
              options={{
                mapId: "aee89872e2f824fdca528c91",
              }}
            >
              {mapLoaded && window.google && window.google.maps && (
                <>
                  {/* Main search location marker — hidden only when the searched insured's
                      own marker is on the map (avoids phantom second pin) */}
                  {markerPosition && !(specificControlNoRecord && addressMarkers.some(m => String(m.insuredData?.ControlNo) === String(specificControlNoRecord?.ControlNo))) && (
                    <>
                      <Marker
                        position={markerPosition}
                        draggable={true}
                        onDragEnd={handleMarkerDragEnd}
                        icon={{
                          url: "http://maps.google.com/mapfiles/ms/icons/red-dot.png",
                          scaledSize: new window.google.maps.Size(40, 40),
                        }}
                      />
                    </>
                  )}

                  {/* Insured location markers — trimmed to the selected radius */}
                  {radiusFilteredMarkers.length > 0 && (
                    <MarkerClusterer
                      options={{
                        imagePath:
                          "https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m",
                        gridSize: 50,
                        minimumClusterSize: 3,
                        maxZoom: 15,
                      }}
                    >
                      {(clusterer) =>
                        radiusFilteredMarkers.map((marker) => (
                          <Marker
                            key={marker.id}
                            position={{ lat: marker.lat, lng: marker.lng }}
                            onClick={() => createInfoWindow(marker)}
                            onMouseOver={() => {
                              const cn = marker?.insuredData?.ControlNo;
                              if (cn) {
                                setHoveredControlNo(cn);
                                scrollRowIntoView(cn);
                              }
                            }}
                            onMouseOut={() => setHoveredControlNo(null)}
                            clusterer={clusterer}
                            icon={{
                              url:
                                marker.id === activeMarkerId
                                  ? "http://maps.google.com/mapfiles/ms/icons/red-dot.png"
                                  : marker?.insuredData?.ControlNo &&
                                    (marker.insuredData.ControlNo ===
                                      hoveredControlNo ||
                                      marker.insuredData.ControlNo ===
                                        hoveredRecord)
                                  ? "http://maps.google.com/mapfiles/ms/icons/yellow-dot.png"
                                  : "http://maps.google.com/mapfiles/ms/icons/blue-dot.png",
                              scaledSize: new window.google.maps.Size(30, 30),
                            }}
                          />
                        ))
                      }
                    </MarkerClusterer>
                  )}
                  {/* Zipcode coverage area */}
                  {mapLoaded &&
                    zipcodeBoundaries.length > 0 &&
                    zipcodeBoundaries.map((boundary, index) => (
                      <Polygon
                        key={`boundary-area-${index}`}
                        paths={boundary.paths}
                        options={{
                          fillColor: boundary.isSingleZipcode
                            ? "#4CAF50"
                            : "#2196F3", // Green for single zipcode, blue for larger area
                          fillOpacity: 0.2,
                          strokeColor: boundary.isSingleZipcode
                            ? "#1B5E20"
                            : "#0d47a1",
                          strokeOpacity: 0.8,
                          strokeWeight: boundary.isSingleZipcode ? 1 : 2,
                        }}
                      />
                    ))}
                  <MapLegend />
                </>
              )}
            </GoogleMap>
          </LoadScript>
        </div>
      </div>

      {/* Bottom slide panel for mobile view only */}
      {isMobileView && (
        <div
          className={`bottom-slide-panel ${isBottomPanelOpen ? "open" : ""}`}
        >
          {selectedInsured && (
            <>
              <div className="panel-header">
                <h5>
                  <strong>
                    {selectedInsured.isLoading ? "Loading..." : "Account:"}
                  </strong>{" "}
                  {!selectedInsured.isLoading
                    ? selectedInsured.InsuredName ||
                      selectedInsured.InsuredPolicyName ||
                      "N/A"
                    : ""}
                </h5>
                <button
                  className="close-btn"
                  onClick={() => setBottomPanelOpen(false)}
                >
                  <IoCloseCircle />
                </button>
              </div>

              <div className="panel-content">
                {selectedInsured.isLoading ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3">Fetching details...</p>
                  </div>
                ) : (
                  <>
                    <ul className="gc-list">
                      <li>
                        <p>
                          <strong>Address:</strong>{" "}
                          {selectedInsured.Address ||
                            getDisplayAddress(selectedInsured)}
                        </p>
                        <p>
                          <strong>Control No:</strong>{" "}
                          {selectedInsured.ControlNo || "N/A"}
                        </p>
                        <p>
                          <strong>Effective Date:</strong>{" "}
                          {formatDate(selectedInsured.EffectiveDate)}
                        </p>
                        <p>
                          <strong>Carrier:</strong>{" "}
                          {selectedInsured.Carrier || "N/A"}
                        </p>
                        <p>
                          <strong>Policy Type:</strong>{" "}
                          {selectedInsured.PolicyType || "N/A"}
                        </p>
                        <p>
                          <strong>Premium:</strong>{" "}
                          {formatMoney(selectedInsured.Premium)}
                        </p>
                        <p>
                          <strong>TIV:</strong>{" "}
                          {formatMoney(
                            selectedInsured.TIV || selectedInsured.PropertyTIV
                          )}
                        </p>
                        <p>
                          <strong>No. of Buildings:</strong>{" "}
                          {selectedInsured.NoOfBuildings || "N/A"}
                        </p>
                        <p>
                          <strong>Status:</strong>{" "}
                          {selectedInsured.Status || "N/A"}
                        </p>
                        <p>
                          <strong>WildFire limit:</strong>{" "}
                          {selectedInsured.WFSelectedLimit || "N/A"}
                        </p>
                      </li>
                    </ul>

                    {/* Carrier breakdown section */}
                    <div
                      className={`carrierBreakdown ${hover ? "hovered" : ""}`}
                      onMouseEnter={() => setHover(true)}
                      onMouseLeave={() => setHover(false)}
                    >
                      <div className="card gc-card mx-3 mb-3">
                        <div className="card-header">
                          {selectedInsured.Carrier || "Unknown Carrier"}
                        </div>
                        <div className="card-body">
                          <p className="d-flex align-items-center justify-content-between mb-2">
                            <strong>TIV</strong>
                            {formatMoney(
                              selectedInsured.TIV || selectedInsured.PropertyTIV
                            )}
                          </p>
                          <p className="d-flex align-items-center justify-content-between mb-0">
                            <strong>Premium</strong>
                            {formatMoney(selectedInsured.Premium)}
                          </p>
                        </div>
                      </div>

                      <ul className="gc-total mx-3">
                        <li>
                          <strong>Total TIV</strong>
                          {formatMoney(
                            selectedInsured.TIV || selectedInsured.PropertyTIV
                          )}
                        </li>
                        <li>
                          <strong>Total Premium</strong>
                          {formatMoney(selectedInsured.Premium)}
                        </li>
                      </ul>
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      )}
      {toasts.map((toast) => (
        <ToastNotification
          key={toast.id}
          message={toast.message}
          type={toast.type}
          onClose={() => removeToast(toast.id)}
        />
      ))}
    </>
  );
}

export default GeographicalConcentration;